// Event obligations per write operation (finding #3). A route's `emits` says what it MAY write;
// this table says what every successful call MUST write (`always`), what it writes at least one
// of (`oneOf`), and when it writes the rest (`when`, with the condition). The two are kept by
// double entry: `obligationDrift` fails when they disagree, so dropping an event from a route
// (or from this table) is a test failure, not a silent gap. services/api may mark a write route
// verified only with emit scenarios: integration runs of the handler whose emitted events
// `checkEmitted` compares with this table, covering every `when` both ways.
import type { EventType } from "../events";
import type { RouteDef, RouteId, routes } from ".";
import { isReadRoute } from "./route";

export type Obligation = {
  always: readonly EventType[];
  /** At least one of these is written (e.g. a PATCH that writes the event of what changed). */
  oneOf?: readonly EventType[];
  /** Conditional events, each with the condition under which it is written. */
  when?: Partial<Record<EventType, string>>;
  /**
   * The one condition under which a successful call writes nothing (M2-01: an idempotent replay
   * such as a repeated `client_id`, an existing DM, an unchanged follow). Only with `always: []`.
   */
  noop?: string;
};

type WriteRouteId = {
  [K in RouteId]: (typeof routes)[K]["method"] extends "get"
    ? never
    : (typeof routes)[K] extends { safe: true }
      ? never
      : K;
}[RouteId];

/** CORE-REVERT: what a revert writes per item it reaches (domain/incidents/revert.ts). */
const REVERT_WHEN = {
  "outbox.cancelled": "a queued, failed or dead send is cancelled",
  "approval.executed": "a cancelled send came from an approval: its outcome becomes not applied",
  "wakeup.requested": "a cancelled send came from an approval a digital employee asked for",
  "task.undone": "an agent write within its undo window is undone",
  "task.status_changed": "an undone write was a status change",
  "task.updated": "an undone write was a title, parent, start or estimate change",
  "task.due_changed": "an undone write was a due date change",
  "task.priority_changed": "an undone write was a priority change",
  "task.field_changed": "an undone write was a field value",
  "task.moved": "an undone write was a move to another board",
  "task.created":
    "a delivered send, or a write that could not be undone, needs a person: the follow-up task (once per incident)",
  "page.created": "the follow-up task is filed: its body page (what to handle by hand)",
} as const;

/** M4-05: what a take-over does to the runs targeting the room (02 "runs park until cleared"). */
const TAKE_OVER_WHEN = {
  "run.parked": "a started run (running or waiting for input) targets the room",
  "run.cancelled":
    "a run not started yet targets the room: it is cancelled and its wake-ups wait for the release",
  "budget.released": "a cancelled not-started run's reservation is released",
} as const;
/** M4-05: the release restores parked runs and wakes the room's turn holder. */
const RELEASE_WHEN = {
  "run.unparked": "the take-over parked a run",
  "agent_room.turn_changed": "the room has agents but no holder yet (its first agent takes it)",
  "wakeup.requested": "the room has a turn holder and is not stalled",
} as const;

/** LOOP-BREAKER: what the loop detector writes when a batch of steps or turns trips it. */
const LOOP_WHEN = {
  "run.loop_warned":
    "the same calls or intent repeat one short of the limit with no progress (once per run)",
  "run.loop_stopped":
    "the cycle repeats the limit, the run stalls past the limit while spending tokens, or the runtime's guard stopped it",
  "run.failed": "the run was stopped as a loop (`loop_stopped`)",
  "budget.settled": "the run was stopped as a loop: its reservation settles at the spend",
} as const;

/** TASK-FILES: an agent's file write is a `tool_call` step of its running run. */
const FILE_STEP_WHEN =
  "the caller is a digital employee's running run (its token, or its agent key naming the run)";
/** TASK-FILES: `folderPath` / `folder_path` makes the folders of the chain the task lacks. */
const FOLDER_CHAIN_WHEN = "the folder path names a folder the task does not have yet";

/** WALLET-P1: the warning, once until the next credit lifts the balance again. */
const WALLET_LOW_WHEN =
  "the wallet is on and the balance ends at or under the warning level, not yet warned";
/** WALLET-P1: the zero crossing (the DEs pause through the `wallet.controls` consumer). */
const WALLET_EMPTY_WHEN = "the wallet is on and the balance goes from above zero to zero or below";

export const eventObligations = {
  "operator.companies.create": {
    always: [
      "company.created",
      "membership.created",
      "access.granted",
      "agency_grant.created",
      "invite.created",
      "metric_def.created",
      "access.revoked",
      "membership.deactivated",
      // M1-06: the seeded templates ("Client onboarding").
      "template.created",
    ],
  },
  "operator.entitlements.put": { always: ["company.entitlement_changed"] },
  // NEW-CLIENT-PROCEDURE: the HTVA operator seats and the client's support channel.
  "operator.supportChannel.create": {
    always: [],
    // the channel made, or an operator added to the one already there
    oneOf: ["channel.created", "channel.member_changed"],
    when: {
      "membership.created": "an operator who is not yet a person of the company",
      "membership.updated": "an operator whose membership was paused or deactivated",
      "access.granted": "an operator who does not hold the member relation yet",
      "invite.accepted": "an operator with a pending invitation to the company",
    },
    noop: "every operator already seated and in the channel",
  },
  // SKILLSMITH-HUB
  "operator.systemAgents.spend": {
    // A new turn with a non-zero cost posts its line.
    always: [],
    oneOf: ["cost.posted"],
    noop: "a replayed turn, or a turn with no cost (it posts nothing)",
  },
  // CANVAS-VOICE: the op row (`cell.canvas_op`, announced on `cos_live`) and its audit event.
  "liveCanvas.op": { always: ["canvas.op_written"] },
  // VOICE-CHOICES: the company default is a company setting; the service opens a session once;
  // a segment is booked once.
  "voice.settings.put": {
    always: [],
    oneOf: ["company.updated"],
    noop: "the body equals the company's stored default voice",
  },
  "voice.sessions.open": {
    always: [],
    oneOf: ["voice_session.opened"],
    noop: "the session is already open (the same person)",
  },
  "voice.usage.record": {
    always: [],
    oneOf: ["cost.posted"],
    noop: "the segment is already booked, or it lasted 0 seconds",
  },
  "operator.agentHealth.fix": {
    // `apply` or `revert`: the new How I work version.
    always: [],
    oneOf: ["digital_employee.updated"],
    noop: "a reject (the report's decision is recorded on the report, not as an event)",
    // AGENT-SPEC: Skillsmith's accepted proposal is a new version of the DE's spec too.
    when: { "agent_spec.versioned": "the DE has an agent spec (its How I work part follows)" },
  },
  // SKILLSMITH-LENS: the one-click actions write the company events their own routes write; a
  // stop, an alert acknowledged or resolved and an operator job retried write none.
  "operator.skillsmith.actions.run": {
    always: [],
    // A schedule paused or resumed writes its recurring_work.updated too (as recurringWork.pause).
    oneOf: [
      "recurring.paused",
      "recurring.resumed",
      "recurring_work.updated",
      "wakeup.requested",
      "digital_employee.resumed",
    ],
    noop: "a stop, an alert acknowledged or resolved, an operator job retried (audited only)",
  },
  "operator.skillsmith.drafts.decide": {
    always: [],
    oneOf: [
      "recurring.paused",
      "recurring.resumed",
      "recurring_work.updated",
      "wakeup.requested",
      "digital_employee.resumed",
    ],
    noop: "a dismissal, an accepted config proposal or ticket, or an action that writes no event",
  },
  "operator.skillsmith.orders.create": {
    // A model call with a known gateway cost posts the System usage (maintenance) line.
    always: [],
    oneOf: ["cost.posted"],
    noop: "no model call (the plain answer), a cached answer, or a call on the company's own key",
  },
  // SKILLSMITH-OPPORTUNITIES: every quote change is the company's event
  "operator.helperOffers.create": { always: ["helper_offer.changed"] },
  "operator.helperOffers.status": { always: ["helper_offer.changed"] },
  "billing.helperOffers.decide": { always: ["helper_offer.changed"] },
  // ADMIN-INSIGHTS: every question asked is logged for its asker; pin and unpin are events
  "insights.ask": { always: ["insight.asked"] },
  "insights.widgets.create": { always: ["insight_widget.changed"] },
  "insights.widgets.delete": { always: ["insight_widget.changed"] },
  "operator.consumers.retry": { always: ["consumer.record_retried"] },
  // CONNECTIONS: recorded in the platform company's log (and `cp.platform_audit`).
  "operator.serviceEndpoints.set": { always: ["service_endpoint.changed"] },
  "operator.serviceEndpoints.reset": { always: ["service_endpoint.changed"] },
  // PROVISION-IN-PRODUCT: the request; the worker's `de.provision` consumer does the set-up.
  "digitalEmployees.provision": { always: ["digital_employee.provision_requested"] },
  // DE-CONNECT: the request; the worker's `de.connect` consumer runs the steps.
  "digitalEmployees.connect": { always: ["digital_employee.connect_requested"] },
  // The satellite took the DE's sealed agent key (wiped here), or only its Recall key again.
  "aigentpanel.deCredentials": { always: ["digital_employee.credentials_claimed"] },
  // OPS-AUTH: the ops account's agency_operator tuple in each granted company (the audit row is
  // `cp.platform_audit`, not an event).
  "operator.opsAccount.issue": {
    always: [],
    oneOf: ["access.granted"],
    noop: "the account is already issued and holds every granted company's tuple",
  },
  "operator.opsAccount.revoke": {
    always: [],
    oneOf: ["access.revoked"],
    noop: "the account was not issued (or holds no company's tuple)",
  },
  // SUPER-ADMIN: opening a client grants the caller's agency_operator tuple there once; ending an
  // assignment revokes the assignee's (the audit rows are `cp.platform_audit`, not events).
  "operator.workspace.clients.open": {
    always: [],
    oneOf: ["access.granted"],
    noop: "the caller already holds operator access in the client",
  },
  "operator.workspace.assignments.revoke": {
    always: [],
    oneOf: ["access.revoked"],
    noop: "the assignee held no operator access in the client (never opened it)",
  },
  // M5-05: every break-glass transition is the company's event too.
  "operator.breakGlass.request": { always: ["access.break_glass_requested"] },
  "operator.breakGlass.approve": { always: ["access.break_glass_started"] },
  "operator.breakGlass.reject": { always: ["access.break_glass_rejected"] },
  "operator.breakGlass.end": { always: ["access.break_glass_ended"] },
  "breakGlass.approve": { always: ["access.break_glass_started"] },
  "breakGlass.reject": { always: ["access.break_glass_rejected"] },
  "breakGlass.end": { always: ["access.break_glass_ended"] },
  "operator.consumers.skip": { always: ["consumer.record_skipped"] },
  "companies.update": {
    always: ["company.updated"],
    when: {
      // FOUNDER-OS (Codex r3 #1, r4 #7): applied in the PATCH's own transaction.
      "portfolio_projection.published":
        "an account holds the company and its Founder OS sharing or head seat changed a stored projection",
    },
  },
  "people.invite": { always: ["invite.created"] },
  "invitations.revoke": { always: ["invite.revoked"] },
  "invitations.accept": {
    always: ["invite.accepted"],
    when: {
      "membership.created": "the invitee had no membership of the company",
      "membership.updated": "the invitee's paused or deactivated membership is reactivated",
      "access.granted": "an invited relation was not held yet",
    },
  },
  "people.update": {
    always: [],
    oneOf: ["membership.updated", "membership.deactivated", "access.granted", "access.revoked"],
    when: {
      // STEP1-OUTBOX
      "connection.grant_changed":
        "a person who leaves had granted their own connection to a DE: those grants end",
    },
  },
  "roles.create": { always: ["role.created"] },
  "roles.update": { always: ["role.updated"] },
  "roles.delete": { always: ["role.deleted"] },
  "seats.create": { always: ["seat.created"] },
  "seats.update": { always: ["seat.updated"] },
  // AWARENESS-MVP: the seat's boards are a seat setting (`settings.boards_you_use`).
  "seats.boardsUpdate": { always: ["seat.updated"] },
  "seats.assign": {
    always: ["seat.assigned", "access.granted"],
    when: { "team.updated": "the new holder changes the composition of a team the seat is in" },
  },
  "seats.unassign": {
    always: ["seat.unassigned", "access.revoked"],
    when: {
      "agent_key.revoked": "the holder is a digital employee with live keys",
      "team.updated": "the ended holder changes the composition of a team the seat is in",
    },
  },
  "seats.close": {
    always: ["seat.closed"],
    when: {
      "access.revoked": "the seat had active holders",
      "agent_key.revoked": "a digital-employee holder had live keys",
      "team.updated": "ending the holders changes the composition of a team the seat is in",
      "recurring_work.updated": "the seat's responsibilities had active recurring work (paused)",
    },
  },
  "seats.putMetrics": { always: ["seat_metric.changed"] },
  // KPI-ASSIGN: one-row writes; the event carries the set after the change, as the PUT's does.
  "seats.putMetric": { always: ["seat_metric.changed"] },
  "seats.removeMetric": { always: ["seat_metric.changed"] },
  // TEAM-KPI: one-row writes; the event carries the team's set after the change.
  "teams.putMetric": {
    always: ["team_metric.changed"],
    when: { "target_rule.changed": "the body sets or clears the team's target" },
  },
  "teams.removeMetric": { always: ["team_metric.changed"] },
  "digitalEmployees.create": {
    // AGENT-RECORD: `seat_metric.changed` with the template's metric set, or with
    // `seed_template_metrics: false` when the seat lacks some of the record's context metrics
    // (a seat a DE held before may carry them all); targets only with the set.
    always: ["membership.created", "digital_employee.created", "seat.assigned", "access.granted"],
    when: {
      "seat_metric.changed":
        "the template's metric set is seeded, or the seat lacks a record metric (`seed_template_metrics: false`)",
      "team.updated": "the DE's assignment changes the composition of a team the seat is in",
      "target_rule.changed":
        "the template's metric set is seeded (not `seed_template_metrics: false`)",
      "metric_def.created":
        "the company predates a catalog entry the DE's seat needs (written first)",
      // AGENT-SPEC: the Add-DE flow names the DE's first agent spec.
      "agent_spec.versioned": "the body carries `agent_spec` (the DE's first signed version)",
    },
  },
  // AGENT-SPEC: a change that leaves the document as it was writes no version.
  "digitalEmployees.putAgentSpec": {
    always: [],
    oneOf: ["agent_spec.versioned"],
    noop: "a change that leaves the document as it was (no new version)",
  },
  "digitalEmployees.update": {
    always: [],
    oneOf: [
      "digital_employee.updated",
      "membership.updated",
      "digital_employee.activated",
      "digital_employee.paused",
      "digital_employee.resumed",
      "digital_employee.retired",
    ],
    when: {
      "seat.unassigned": "retiring a DE that holds a seat ends the assignment",
      "access.revoked": "retiring a DE that holds a seat ends the assignment",
      "agent_key.revoked": "retiring a DE with live keys revokes them",
      "team.updated": "retiring a DE that holds a seat changes the composition of its teams",
    },
  },
  // AGENT-MENTION: remove = retire (when not yet) + revoke every key + hide from the roster.
  "digitalEmployees.remove": {
    always: [],
    oneOf: ["digital_employee.removed"],
    noop: "the DE was already removed",
    when: {
      "digital_employee.retired": "the DE was not retired yet",
      "membership.updated": "the DE was not retired yet (its membership is deactivated)",
      "seat.unassigned": "the DE still held a seat",
      "access.revoked": "the DE still held a seat",
      "agent_key.revoked": "the DE had live keys",
      "team.updated": "the DE still held a seat (its teams' composition changes)",
    },
  },
  // PURGE-RETIRED-AGENTS: a retired DE removed for good.
  "digitalEmployees.purge": {
    always: ["agent.purged"],
    when: {
      "task.unassigned": "it held a seat nobody holds now with open tasks: they leave that seat",
      "task.assigned":
        "it held a seat nobody holds now with open tasks: they move up the reporting chain",
      "run.cancelled": "it had runs not ended yet",
      "budget.released": "a cancelled run had not started (its reservation is released)",
      "budget.settled": "a cancelled run had started (its reservation settles)",
    },
  },
  "digitalEmployees.createKey": { always: ["agent_key.created"] },
  "digitalEmployees.revokeKey": { always: ["agent_key.revoked"] },
  "digitalEmployees.createLitellmKey": {
    always: [],
    oneOf: ["digital_employee.litellm_key_created", "digital_employee.provisioning_failed"],
    when: {
      "agent_key.created": "the key is provisioned: the puller's ingest key (ingest:cost only)",
    },
  },
  "digitalEmployees.ackLitellmKey": {
    always: ["digital_employee.litellm_key_delivered"],
    when: { "digital_employee.activated": "the delivered key completes setup of a DE" },
  },
  "externalIdentities.import": {
    always: ["import.committed"],
    when: {
      "external_identity.created": "a row maps a new external identity",
      "external_identity.closed": "a row ends an existing mapping",
    },
  },
  "metrics.create": { always: ["metric_def.created"] },
  "metrics.update": { always: ["metric_def.updated"] },
  "metrics.archive": { always: ["metric_def.archived"] },
  "metrics.unarchive": { always: ["metric_def.unarchived"] },
  "targets.create": { always: ["target_rule.changed"] },
  "targets.update": { always: ["target_rule.changed"] },
  "targets.delete": { always: ["target_rule.changed"] },
  "observations.create": { always: ["observation.recorded"] },
  "observations.delete": { always: ["observation.recorded"] },
  "observations.import": {
    always: ["import.committed"],
    when: { "observation.recorded": "the file has accepted rows" },
  },
  "scorecards.publish": { always: ["scorecard.published"] },
  "scorecards.revise": { always: ["scorecard.published"] },
  "costs.create": { always: ["cost.posted"] },
  "costs.reverse": { always: ["cost.posted"] },
  "costs.settleMeter": {
    always: ["cost.meter_settled"],
    when: { "cost.posted": "`repost`: the reversal and the re-post" },
  },
  "costs.import": {
    always: ["import.committed"],
    when: { "cost.posted": "the file has accepted rows" },
  },
  "deSubscriptions.create": { always: ["de_subscription.created"] },
  "deSubscriptions.update": { always: ["de_subscription.changed"] },
  // OOO-DELEGATE
  "ooo.set": { always: ["ooo.window_set"] },
  "ooo.end": { always: [], oneOf: ["ooo.window_ended"], noop: "the window had already ended" },
  "ooo.dismissSummary": { always: ["ooo.summary_seen"] },
  // lane B: REQUEST-IDENTITY / REQUEST-RECOVERY
  "requests.decide": {
    always: [],
    oneOf: ["request.accepted", "request.rejected", "wakeup.requested"],
    noop: "the request was already decided that way",
  },
  "recoveries.resolve": {
    always: [],
    oneOf: ["recovery.resolved"],
    when: { "wakeup.requested": "Continue with no held wake: the DE is woken on the request" },
    noop: "the same action on a resolved recovery returns the first result",
  },
  "timeEntries.create": {
    always: ["time_entry.created"],
    when: { "cost.posted": "the entry is approved and the person has a cost rate" },
  },
  "timeEntries.update": {
    always: [],
    oneOf: ["time_entry.updated", "time_entry.approval_changed"],
    when: { "cost.posted": "approved seconds changed and the person has a cost rate" },
  },
  "timeEntries.delete": {
    always: ["time_entry.deleted"],
    when: { "cost.posted": "the voided entry had posted human-time cost" },
  },
  "timeEntries.import": {
    always: ["import.committed"],
    when: {
      "time_entry.created": "a row is new",
      "time_entry.updated": "a row revises an existing entry",
      "time_entry.approval_changed": "a row changes an entry's approval status",
      "time_entry.deleted": "a row voids an entry (seconds 0)",
      "external_identity.created": "the identities part maps a new external id",
      "external_identity.closed": "the identities part remaps an external id",
      "cost.posted": "approved seconds changed and the person has a cost rate",
    },
  },
  "imports.reverse": {
    always: ["import.reversed"],
    when: {
      "time_entry.deleted": "the import created time entries",
      "observation.recorded": "the import recorded observations",
      "cost.posted": "the import posted costs",
      "external_identity.closed": "the import mapped identities",
      "task.deleted": "the import created tasks that still exist",
      "page.archived": "a task it deletes has a body page (archived with it)",
    },
  },
  "reports.generate": {
    always: ["friday_report.generated"],
    when: { "scorecard.published": "the week's scorecards were not yet published" },
  },
  "reports.update": { always: ["friday_report.updated"] },
  "reports.send": { always: [], oneOf: ["friday_report.sent", "friday_report.send_failed"] },
  "reports.reconcile": { always: ["friday_report.delivery_reconciled"] },
  // REPORTS-UI
  "reports.setSubscription": {
    always: [],
    oneOf: ["company.updated"],
    noop: "the caller's entry of the opt-out list is already as asked",
  },
  // M1-02
  "teams.create": { always: ["team.created"] },
  "teams.update": { always: ["team.updated"] },
  "teams.delete": {
    always: ["team.deleted"],
    when: { "workforce_agent.updated": "agents (AG) of the team are left with no team" },
  },
  "teams.addMember": {
    always: ["team.member_changed"],
    when: { "team.updated": "the seat's holders change the team's derived composition" },
  },
  "teams.removeMember": {
    always: ["team.member_changed"],
    when: { "team.updated": "the seat's holders change the team's derived composition" },
  },
  "orgChart.putEdges": {
    always: [],
    oneOf: ["seat_edge.changed"],
    when: { "seat.updated": "a reports_to edge moved a seat's supervisor_seat_id" },
  },
  "orgChart.putOrder": {
    always: [],
    oneOf: ["seat.updated"],
    noop: "the seats are already in that order",
  },
  "responsibilities.create": { always: ["responsibility.created"] },
  "responsibilities.update": {
    always: ["responsibility.updated"],
    when: { "recurring_work.updated": "archiving paused active recurring work" },
  },
  "responsibilities.delete": { always: ["responsibility.removed"] },
  // CHART-KB
  "goals.create": { always: ["goal.created"] },
  "goals.update": {
    always: ["goal.updated"],
    when: {
      "goal.track_changed": "the track moved",
      "goal.milestone_done": "a milestone was marked done",
    },
  },
  "goals.delete": { always: ["goal.deleted"] },
  // KPI-VISIBILITY
  "kpiVisibility.create": { always: ["kpi_grant.changed"] },
  "kpiVisibility.update": { always: ["kpi_grant.changed"] },
  "kpiVisibility.delete": { always: ["kpi_grant.changed"] },
  // PRIORITIES-HOME
  "priorities.carryOver": {
    always: [],
    // Each copy writes goal.created into the next quarter and goal.updated closing the original.
    oneOf: ["goal.created"],
    when: { "goal.updated": "the carried priority was closed as missed in its quarter" },
    noop: "no open priority on the caller's writable seats is left to carry (or each is already in the next quarter)",
  },
  // M8-ASSESS: assessment rounds, respondents, answers, reports and proposals.
  "assessments.createRound": {
    always: ["assessment.round_created"],
    when: {
      "assessment.participant_invited": "the body names at least one member or guest",
      "assessment.round_opened": "`open: true`",
    },
  },
  "assessments.updateRound": {
    always: [],
    oneOf: ["assessment.round_updated"],
    noop: "nothing changed (the same name and lifetime)",
  },
  "assessments.deleteRound": { always: ["assessment.round_deleted"] },
  "assessments.addParticipants": {
    always: [],
    oneOf: ["assessment.participant_invited"],
    noop: "everyone named is already asked in the round",
  },
  "assessments.removeParticipant": { always: ["assessment.participant_removed"] },
  "assessments.openRound": { always: ["assessment.round_opened"] },
  "assessments.remind": { always: ["assessment.participant_reminded"] },
  "assessments.closeRound": {
    always: ["assessment.round_closed", "assessment.report_generated"],
  },
  "assessments.decideProposal": {
    always: [],
    oneOf: ["assessment.proposal_decided"],
    noop: "the proposal is already in that state",
  },
  "assessments.apply": {
    always: [],
    oneOf: ["assessment.proposal_applied"],
    when: {
      "role.created": "an applied seat's role does not exist yet (by name)",
      "seat.created": "an applied proposal is a seat",
      "responsibility.created": "an applied proposal is a responsibility",
      "metric_def.created": "an applied KPI's metric key does not exist yet",
      "seat_metric.changed": "an applied proposal is a KPI (it joins its seat's set)",
    },
    noop: "no accepted proposal is left to apply",
  },
  "assessments.myLink": { always: ["assessment.link_issued"] },
  "respond.save": { always: ["assessment.response_saved"] },
  "respond.submit": { always: ["assessment.response_submitted"] },
  // MEETINGS (06 §Cadence): issues, meeting series, meetings, the vision page.
  "issues.create": { always: ["issue.raised"] },
  "issues.update": {
    always: [],
    oneOf: ["issue.updated", "issue.status_changed", "issue.dropped"],
    noop: "nothing changed (the same values, or the card dropped where it was)",
  },
  "issues.delete": { always: ["issue.deleted"] },
  "issues.escalate": {
    always: [],
    oneOf: ["issue.escalated", "issue.deescalated"],
    noop: "the issue is already at that level",
  },
  "issues.resolve": {
    always: ["issue.resolved"],
    when: {
      "task.created": "kind task: one per to-do",
      "decision.recorded": "kind decision: the decision is logged",
    },
  },
  "meetingSeries.create": { always: ["meeting_series.changed", "meeting.scheduled"] },
  "meetingSeries.update": {
    always: [],
    oneOf: ["meeting_series.changed"],
    noop: "nothing changed",
  },
  "meetings.create": { always: ["meeting.scheduled"] },
  "meetings.update": {
    always: [],
    oneOf: ["meeting.updated", "meeting.rated"],
    noop: "nothing changed",
  },
  "meetings.buildAgenda": { always: ["meeting.agenda_built"] },
  "meetings.addItem": { always: ["meeting.agenda_changed"] },
  "meetings.updateItem": {
    always: [],
    oneOf: ["meeting.agenda_changed"],
    noop: "nothing changed",
  },
  "meetings.removeItem": { always: ["meeting.agenda_changed"] },
  "meetings.start": {
    always: ["meeting.started"],
    when: { "meeting.agenda_built": "the agenda was never built: it is built first" },
  },
  "meetings.cancel": {
    always: ["meeting.cancelled"],
    when: {
      "meeting.scheduled":
        "the series has no meeting after the cancelled one yet: its next one is scheduled",
    },
  },
  "meetings.end": {
    always: ["meeting.ended"],
    when: {
      "meeting.rated": "a rating is given",
      "meeting.updated": "notes are given and differ from the saved ones",
      "message.created": "the series has a channel (the team's) to post the summary in",
      "topic.created": 'the topic "Meeting {date}" is new in that channel',
      "meeting.scheduled": "the series has no later meeting yet: the next one is scheduled",
    },
  },
  "meetings.recordOutcome": {
    always: ["meeting.outcome_recorded"],
    when: {
      "task.created": "kind task: the to-do",
      "decision.recorded": "kind decision: the decision is logged",
      "issue.resolved": "kind issue_resolved: the issue is solved",
      "issue.raised": "kind issue_raised: the issue joins the team's list",
    },
  },
  "meetings.requestPreread": {
    always: ["meeting.preread_requested"],
    when: { "wakeup.requested": "an agent of the team is live: its wake-up is queued" },
  },
  // RECALL-KPIS-MIRROR: every stance set is recorded, whatever memory did with it.
  "decisionStances.set": { always: ["decision.stance_recorded"] },
  "vision.update": {
    always: [],
    oneOf: ["vision_page.updated"],
    when: {
      "page.created": "the vision page did not exist yet",
      "page.edited": "the vision page existed",
    },
    noop: "no section changed",
  },
  // CALENDAR-C1 (docs/design/2026-10-28-calendar.md): calendars, shares, follows, events, the feed.
  "calendars.update": { always: [], oneOf: ["calendar.updated"], noop: "nothing changed" },
  "calendarShares.create": {
    always: [],
    oneOf: ["calendar.shared"],
    noop: "the grantee already has that level",
  },
  "calendarShares.delete": { always: ["calendar.share_revoked"] },
  "calendars.publish": {
    always: [],
    oneOf: ["calendar.published"],
    noop: "the view is already published (its calendar is returned)",
  },
  "calendars.unpublish": { always: ["calendar.unpublished"] },
  "calendarFollows.create": {
    always: [],
    oneOf: ["calendar.follow_changed"],
    noop: "already followed, shown and coloured so",
  },
  "calendarFollows.update": {
    always: [],
    oneOf: ["calendar.follow_changed"],
    noop: "nothing changed",
  },
  "calendarFollows.delete": {
    always: [],
    oneOf: ["calendar.follow_changed"],
    noop: "the calendar was not followed",
  },
  "calendarEvents.create": { always: ["calendar_event.created"] },
  "calendarEvents.update": {
    always: [],
    oneOf: ["calendar_event.updated"],
    when: {
      "calendar_event.created":
        "scope following on a later occurrence: the series is split and the rest starts anew",
    },
    noop: "nothing changed",
  },
  "calendarEvents.delete": {
    always: [],
    oneOf: ["calendar_event.cancelled"],
    noop: "the event or occurrence is already cancelled",
  },
  "calendarEvents.rsvp": {
    always: [],
    oneOf: ["calendar_event.responded"],
    noop: "the same answer again",
  },
  "calendarChanges.create": {
    always: [],
    oneOf: [
      "approval.requested",
      "calendar_event.created",
      "calendar_event.updated",
      "calendar_event.cancelled",
    ],
    noop: "an update applied directly that changed nothing",
  },
  "calendarFeed.create": { always: ["calendar_feed.created"] },
  "calendarFeed.delete": {
    always: [],
    oneOf: ["calendar_feed.revoked"],
    noop: "there was no live link",
  },
  "recurringWork.create": { always: ["recurring_work.created"] },
  "recurringWork.update": { always: ["recurring_work.updated"] },
  "recurringWork.delete": { always: ["recurring_work.deleted"] },
  // M1-05 + M2-03's task hook (`domain/boards/template-hooks.ts`, registered by
  // `registerWorkModules`).
  "recurringWork.materialiseNow": {
    always: ["recurring_work.materialised"],
    when: {
      "task.created":
        "the recurring work names a live board (its task template materialises a task)",
      "page.created": "the materialised task's template has a description (M7-02: its page)",
    },
  },
  "templates.create": { always: ["template.created"] },
  "templates.update": { always: ["template.updated"] },
  "templates.delete": { always: ["template.deleted"] },
  "templates.apply": {
    always: ["template.applied"],
    when: {
      "role.created": "the spec has roles the company has no role of the same name for",
      "team.created": "the spec has teams the company has no team of the same name for",
      "team.member_changed": "a spec seat names a team",
      "seat.created": "the spec has seats",
      "responsibility.created": "the spec has responsibilities",
      "recurring_work.created": "a spec responsibility has recurring work",
      // M2-02 / M2-03's template handlers (`domain/templates/work-hooks.ts`).
      "space.created": "the spec has spaces",
      "channel.created": "the spec has spaces (each gets its default channel) or boards",
      "channel.member_changed":
        "a spec space is public (CHAT-UX: its default channel joins the company's people)",
      "board.created": "a spec space has boards",
      "view.created": "a spec space has boards (each gets its default views)",
      // No `field_def.created`: a template board is a blank board, which defines no fields.
      "task.created": "a spec board has starter tasks",
      "page.created": "a starter task has a description (M7-02: its page)",
    },
  },
  "webhooks.create": { always: ["webhook.created"] },
  "webhooks.update": {
    always: ["webhook.updated"],
    when: { "webhook.resumed": "`status: active` on a paused or disabled hook" },
  },
  // WOS-BUNDLE
  "webhooks.resume": {
    always: [],
    oneOf: ["webhook.resumed"],
    noop: "the hook is already active",
  },
  "tags.create": { always: ["tag.created"] },
  "tags.update": {
    always: [],
    oneOf: ["tag.updated"],
    noop: "the name and colour are already as asked",
  },
  "tags.merge": {
    always: ["tag.merged"],
    when: { "task.tagged": "a task carried the merged tag but not the kept one" },
  },
  "tags.delete": { always: ["tag.deleted"] },
  "tasks.addTag": {
    always: [],
    oneOf: ["task.tagged"],
    when: { "tag.created": "the name matched no tag (ignoring case)" },
    noop: "the task already carries the tag",
  },
  "tasks.removeTag": { always: ["task.untagged"] },
  "tasks.addLink": {
    always: [],
    // relates-to / duplicates: `task.linked` on both tasks; blocks / blocked-by: a hard dependency.
    oneOf: ["task.linked", "task.dependency_changed"],
  },
  "tasks.removeLink": {
    always: [],
    // relates-to / duplicates: `task.unlinked` on both tasks; blocks / blocked-by: a hard dependency.
    oneOf: ["task.unlinked", "task.dependency_changed"],
  },
  "tasks.duplicate": {
    always: ["task.created", "task.duplicated"],
    when: {
      "page.created": "the task has a description (its page is copied)",
      "task.tagged": "the task carries tags",
      "task.field_changed": "the task has field values",
      "task.checklist_changed": "checklists are copied and the task has some",
      "task.assigned": "assignees are kept and the task has some",
    },
  },
  "boards.duplicate": {
    always: ["board.created", "board.duplicated", "channel.created", "view.created"],
    when: {
      "field_def.created": "the board has fields",
      "task.created": "tasks are copied and the board has some",
      "page.created": "a copied task has a description",
      "task.tagged": "a copied task carries tags",
      "task.checklist_changed": "a copied task has checklists",
    },
  },
  "spaces.duplicate": {
    always: ["space.created", "space.duplicated", "channel.created"],
    when: {
      "access.granted": "the space is private (the caller owns the copy)",
      "channel.member_changed": "the space is public (its default channel joins the company)",
      "board.created": "the space has boards",
      "board.duplicated": "the space has boards",
      "view.created": "the space has boards",
      "field_def.created": "a board has fields",
      "task.created": "tasks are copied and a board has some",
      "page.created": "a copied task has a description",
      "task.tagged": "a copied task carries tags",
      "task.checklist_changed": "a copied task has checklists",
    },
  },
  "favorites.add": {
    always: [],
    oneOf: ["favorite.added"],
    noop: "the caller already starred it",
  },
  "favorites.remove": { always: ["favorite.removed"] },
  "favorites.reorder": { always: ["favorite.reordered"] },
  "recents.record": {
    always: [],
    oneOf: ["recent.viewed"],
    noop: "it is already the caller's most recent item",
  },
  "mySections.create": { always: ["my_task_section.created"] },
  "mySections.update": {
    always: [],
    oneOf: ["my_task_section.updated"],
    noop: "the name and place are already as asked",
  },
  "mySections.delete": { always: ["my_task_section.deleted"] },
  "my.placeTask": {
    always: [],
    oneOf: ["my_task.placed"],
    noop: "the task is already there",
  },
  // The caller books its own time: the entry lands `pending` (nobody decides their own time),
  // so nothing is costed until a manager approves it (`timeEntries.update`).
  "tasks.logTime": { always: ["time_entry.created"] },
  "tasks.deleteTime": {
    always: ["time_entry.deleted"],
    when: { "cost.posted": "the voided entry had posted human-time cost" },
  },
  "timer.start": {
    always: ["task.timer_started"],
    when: {
      "task.timer_stopped": "the caller's timer ran (on any task): it is stopped and booked first",
      "time_entry.created": "the caller's timer ran (on any task): it is stopped and booked first",
    },
  },
  "timer.stop": {
    always: [],
    oneOf: ["task.timer_stopped"],
    when: {
      "time_entry.created": "a timer ran (its time is booked)",
    },
    noop: "no timer runs",
  },
  "webhooks.delete": { always: ["webhook.deleted"] },
  "webhooks.test": { always: ["webhook.tested"] },
  "webhooks.rotateSecret": { always: ["webhook.secret_rotated"] },
  "webhooks.retryDelivery": { always: ["webhook.delivery_retried"] },
  // WEBHOOKS-IN: incoming webhooks (the delivery's task is made by the `inbound.task` consumer).
  "inboundWebhooks.create": { always: ["inbound_webhook.created"] },
  "inboundWebhooks.update": {
    always: [],
    oneOf: ["inbound_webhook.updated"],
    when: {
      "inbound_webhook.paused": "`status: paused` on an active hook",
      "inbound_webhook.resumed": "`status: active` on a paused or disabled hook",
      "inbound_webhook.disabled": "`status: disabled` on an active or paused hook",
    },
    noop: "nothing differs from the hook as it is",
  },
  "inboundWebhooks.delete": { always: ["inbound_webhook.deleted"] },
  "inboundWebhooks.rotateSecret": { always: ["inbound_webhook.secret_rotated"] },
  "inboundWebhooks.replayDelivery": { always: ["inbound_webhook.received"] },
  "inboundWebhooks.sample": { always: ["inbound_webhook.received"] },
  "inboundWebhooks.receive": {
    always: [],
    oneOf: ["inbound_webhook.received"],
    noop: "the same delivery again (its delivery id, event id or signature was seen)",
  },
  // WALLET-P1: prepaid AI credits (the debit and the pause are consumers: `wallet.debit`,
  // `wallet.controls`).
  "wallet.topUp": { always: ["wallet.checkout_started"] },
  "wallet.updateSettings": {
    always: [],
    oneOf: ["wallet.settings_changed"],
    when: { "wallet.low": WALLET_LOW_WHEN },
    noop: "the warning level is the one already set",
  },
  "stripe.webhook": {
    always: [],
    // WALLET-P1 — credited: a paid checkout of ours (with any refund Stripe already made,
    // refunded too); refunded: a succeeded or pending refund not booked yet; adjusted: a booked
    // refund that failed or was cancelled at Stripe, given back.
    oneOf: [
      "wallet.credited",
      "wallet.refunded",
      "wallet.adjusted",
      // BILLING-PLANS: the invoice mirror and the subscription's state
      "invoice.finalized",
      "invoice.paid",
      "invoice.payment_failed",
      "invoice.voided",
      "invoice.marked_uncollectible",
      "subscription.updated",
      "subscription.cancelled",
      "quote.changed",
      "billing.account_changed",
    ],
    when: {
      "wallet.low": WALLET_LOW_WHEN,
      "wallet.empty": WALLET_EMPTY_WHEN,
      "invoice.created": "the mirror sees a Stripe invoice for the first time",
      "subscription.past_due":
        "a subscription invoice's payment failed and the subscription was not already past due",
      "subscription.restored":
        "the paid (or voided) invoice was the last one holding the company past due or suspended",
      "digital_employee.resumed": "the company is restored and had DEs paused for `subscription`",
      "de_maintenance.changed":
        "the subscription was deleted in Stripe: each DE's maintenance line ends",
      "cost.posted": "a paid subscription invoice carrying DE maintenance (posted per DE)",
    },
    noop: "an event neither the wallet nor billing acts on, one already applied (its meter or event id is booked), or one older than the state it would change",
  },
  "operator.tariffs.create": { always: ["tariff_catalog.changed"] },
  "operator.tariffs.retire": { always: ["tariff_catalog.changed"] },
  "operator.topupBonusTiers.create": { always: ["topup_bonus_tier.changed"] },
  "operator.topupBonusTiers.update": { always: ["topup_bonus_tier.changed"] },
  "operator.topupBonusTiers.delete": { always: ["topup_bonus_tier.changed"] },
  "operator.wallet.putSettings": {
    always: [],
    oneOf: ["wallet.settings_changed"],
    when: {
      "wallet.low": WALLET_LOW_WHEN,
      "tariff.changed":
        "switched on while the platform list has a version the company does not have yet (mirrored)",
    },
    noop: "the switch and the limits are the ones already set",
  },
  "operator.wallet.adjust": {
    always: ["wallet.adjusted"],
    when: { "wallet.low": WALLET_LOW_WHEN, "wallet.empty": WALLET_EMPTY_WHEN },
  },
  "operator.wallet.refund": {
    always: ["wallet.refunded"],
    when: { "wallet.low": WALLET_LOW_WHEN, "wallet.empty": WALLET_EMPTY_WHEN },
  },
  // BILLING-LEDGER
  "operator.wallet.ledgerTopUp": {
    always: ["wallet.credited"],
    when: {
      "wallet.checkout_started":
        "an amount: the operator's own top-up (not the client's waiting one)",
    },
  },
  "operator.wallet.createTariff": { always: ["tariff.changed"] },
  "operator.wallet.retireTariff": { always: ["tariff.changed"] },
  // M2-01: spaces, chat, attachments, follows, inbox
  "spaces.create": {
    always: ["space.created", "channel.created"],
    when: {
      "access.granted": "the space is private (its creator becomes `space#owner`)",
      "channel.member_changed":
        "the space is public (CHAT-UX: its default channel joins the company's people)",
    },
  },
  "spaces.update": { always: ["space.updated"] },
  "spaces.archive": { always: ["space.archived"] },
  "spaces.unarchive": { always: ["space.unarchived"] },
  "spaces.addMember": { always: ["access.granted"] },
  "spaces.removeMember": {
    always: ["access.revoked"],
    when: { "follow.changed": "the member followed something inside the space" },
  },
  "channels.create": {
    always: ["channel.created"],
    when: {
      "channel.member_changed": "`member_ids` names others than the creator",
      // MULTI-SPACE-ACCESS
      "channel.shares_changed": "`shares` names a space to share it into",
    },
  },
  "channels.shares.set": {
    always: [],
    oneOf: ["channel.shares_changed"],
    noop: "the shares the caller may change are already the ones named",
  },
  "channels.update": { always: ["channel.updated"] },
  "channels.archive": { always: ["channel.archived"] },
  // CHAT-CONTROLS: restore, delete (a tombstone) and a member's channel role.
  "channels.unarchive": { always: ["channel.restored"] },
  "channels.delete": { always: ["channel.deleted"] },
  "channels.updateMember": { always: ["channel.member_changed"] },
  "channels.createTopic": {
    always: [],
    oneOf: ["topic.created"],
    noop: "a topic of that name already exists (it is returned)",
  },
  "channels.addMember": { always: ["channel.member_changed"] },
  "channels.removeMember": {
    always: ["channel.member_changed"],
    when: { "follow.changed": "the member followed the channel or its topics" },
  },
  // M2-02: channel subscriptions.
  "channels.join": {
    always: [],
    oneOf: ["channel.member_changed"],
    noop: "the caller already joined the channel (its subscription is returned)",
  },
  "channels.leave": {
    always: ["channel.member_changed"],
    when: { "follow.changed": "the caller followed the channel or one of its topics" },
  },
  "channels.subscription": {
    always: [],
    oneOf: ["channel.member_changed", "follow.changed"],
    noop: "nothing the patch names differs from the subscription",
  },
  "channels.takeOver": { always: ["agent_room.taken_over"], when: TAKE_OVER_WHEN },
  "channels.release": { always: ["agent_room.released"], when: RELEASE_WHEN },
  "channels.read": {
    always: [],
    oneOf: ["channel.read"],
    noop: "the position would move backwards (read positions never do), or the caller has not joined the public channel (it has no position to move)",
  },
  "topics.rename": { always: ["topic.renamed"] },
  "topics.resolve": { always: ["topic.resolved"] },
  "topics.pin": { always: ["topic.pinned"] },
  "topics.archive": { always: ["topic.archived"] },
  "topics.restore": { always: ["topic.archived"] },
  "topics.delete": { always: ["topic.deleted"] },
  "topics.move": { always: ["topic.moved"] },
  "topics.split": { always: ["topic.split", "topic.created"] },
  "topics.subscription": {
    always: [],
    oneOf: ["follow.changed"],
    noop: "the caller already follows (or does not follow) the topic",
  },
  "topics.updateMembers": {
    always: [],
    oneOf: ["topic.member_changed"],
    noop: "everyone added is already a member and everyone removed is not one",
  },
  "topics.privacy": {
    always: [],
    oneOf: ["topic.privacy_changed"],
    noop: "the topic already is (or is not) private",
    when: {
      "topic.member_changed":
        "a non-member made it private (the opener or a manager): they become a member, so they still see it",
    },
  },
  "topics.takeOver": { always: ["agent_room.taken_over"], when: TAKE_OVER_WHEN },
  "topics.release": { always: ["agent_room.released"], when: RELEASE_WHEN },
  "messages.create": {
    always: [],
    oneOf: ["message.created"],
    noop: "the `client_id` was already posted (the first message is returned)",
    when: {
      "wakeup.requested":
        "the message mentions an agent (DE or crew) that may read its thread, or it is posted in an agent room with a turn to hand (M4-05), or the channel's agent rule is `topic` and it has agent members (M2-04); never under `silent`",
      "wakeup.dropped":
        "the message mentions an agent that cannot be woken now (AGENT-MENTION): still in setup or retired, paused, unable to read the thread, under the channel's `silent` rule, or the Agents add-on is off; its wake-up is written dropped with the reason",
      "approval.requested":
        "the poster is a DE whose seat holds its messages for approval (the thread gate, M3-03)",
      "agent_room.turn_changed":
        "the message is in an agent room (M4-05) and moves the turn: the holder's post hands it on, a person's post restarts a stalled room or gives a room its first holder",
      "attachment.attached":
        "the message carries staged files (CHAT-FILES `attachment_ids`) and is not held for approval",
    },
  },
  "messages.bookmark": {
    always: [],
    oneOf: ["message.bookmark_changed"],
    noop: "the bookmark is already as asked",
  },
  // CHAT-SWITCH-A
  "chatStatus.set": {
    always: [],
    oneOf: ["member_status.changed"],
    noop: "the status and time zone are already as asked",
  },
  "chatReminders.create": { always: ["reminder.created"] },
  // CHAT-SWITCH-B
  "chatAsks.create": { always: ["chat_ask.requested", "wakeup.requested"] },
  "agentChatAsks.answer": { always: ["chat_ask.answered"] },
  // DE-INITIATE
  "deInitiations.create": {
    always: [],
    oneOf: ["message.created"],
    noop: "refused, queued for the end of quiet hours, or a retried `client_id` (its row is the record; nothing is posted now)",
    when: {
      "topic.created":
        "a new thread in a channel (not a collapsed reply), or a new DM (its `(no topic)` topic)",
      "channel.created": "a DM the DE opens with the person for the first time",
      "approval.requested":
        "the DE's seat holds its messages for approval (the thread gate, M3-03): the post is the card",
    },
  },
  // DE-BRIEFS: each settings PUT records the level it set (or cleared); a brief posts as a
  // DE-started DM.
  "briefSettings.putCompany": { always: ["brief_settings.updated"] },
  "briefSettings.putMine": { always: ["brief_settings.updated"] },
  "briefSettings.putDe": { always: ["brief_settings.updated"] },
  "agentBriefs.send": {
    always: [],
    oneOf: ["message.created"],
    noop: "queued for the end of the person's quiet hours (posted by the brief job then)",
    when: {
      "topic.created": "a new DM (its `(no topic)` topic)",
      "channel.created": "a DM the DE opens with the person for the first time",
      "approval.requested":
        "the DE's seat holds its messages for approval (the thread gate, M3-03): the post is the card",
    },
  },
  // COS-BRIEF-RULES: every change is audited; the CoS's change is confirmed in the CEO's thread.
  "briefRules.put": { always: ["brief_rule.updated"] },
  "briefRules.delete": { always: ["brief_rule.updated"] },
  "agentBriefRules.set": { always: ["brief_rule.updated", "message.created"] },
  "agentBriefRules.remove": { always: ["brief_rule.updated", "message.created"] },
  "chatWebhooks.create": { always: ["chat_webhook.created"] },
  "chatWebhooks.createForDe": {
    always: ["chat_webhook.created"],
    when: {
      "channel.created": "the admin's webhook DM with the DE is new (opened for its posts)",
      "topic.created": "the DM is new (its `(no topic)` topic, where the posts go)",
    },
  },
  "chatWebhooks.rotate": { always: ["chat_webhook.rotated"] },
  "chatWebhooks.revoke": {
    always: [],
    oneOf: ["chat_webhook.revoked"],
    noop: "the webhook was revoked already",
  },
  "chatWebhooks.receive": {
    always: ["message.created"],
    when: {
      "topic.created": "a channel hook's first post: its topic (the hook's name) is new",
      "wakeup.requested": "a DE hook, with the DE active and agents on: the post wakes it",
    },
  },
  "scheduledMessages.create": { always: ["scheduled_message.created"] },
  "scheduledMessages.update": { always: ["scheduled_message.updated"] },
  "scheduledMessages.cancel": { always: ["scheduled_message.cancelled"] },
  "messages.hidePreview": {
    always: [],
    oneOf: ["message.preview_hidden"],
    noop: "the card was removed already",
  },
  "chatReminders.dismiss": {
    always: [],
    oneOf: ["reminder.dismissed"],
    noop: "the reminder was dismissed already",
  },
  // SEARCH-NOTIF
  "notifications.setPrefs": {
    always: [],
    oneOf: ["notification_prefs.changed"],
    noop: "the preferences are already as asked",
  },
  // DE-REPLY-STYLE
  "replyStyle.set": {
    always: [],
    oneOf: ["reply_style.changed"],
    noop: "the reply length and quick replies (DE-QUICK-ACK) are already as asked",
  },
  // W1-FIXES
  "replyStyle.setFromMessage": {
    always: [],
    oneOf: ["reply_style.changed"],
    noop: "the person's reply length is already as asked",
  },
  // HARDEN-pwa
  "push.subscribe": {
    always: [],
    oneOf: ["push_subscription.added"],
    noop: "this browser is already subscribed for the caller with the same keys",
  },
  "push.unsubscribe": {
    always: [],
    oneOf: ["push_subscription.removed"],
    noop: "this browser is not subscribed in the company",
  },
  // SCREENS-S1
  "screens.open": { always: [], oneOf: ["screen_lease.queued", "screen_lease.started"] },
  "screens.takeOver": { always: ["screen_lease.taken_over"] },
  "screens.handBack": {
    always: ["screen_lease.handed_back"],
    when: {
      "browser_account.status_changed":
        "the screen waited on a person for its account (needs you): the account is live again",
      "screen_ask.resolved": "the screen had an open needs-you ask: it is done by this person",
      "message.created":
        "the ask's agent run ended meanwhile: a note beside the card says it was handed back",
      "wakeup.requested": "the ask's agent run ended meanwhile: the agent is woken to carry on",
    },
  },
  "screens.askNotNow": { always: ["screen_ask.resolved"] },
  "screens.askRestart": { always: ["message.created", "wakeup.requested"] },
  "screens.askExtend": { always: ["screen_ask.extended"] },
  "screens.bump": { always: ["screen_lease.bumped"] },
  "screens.stop": {
    always: ["screen_lease.ended"],
    when: {
      "screen_lease.started": "a waiting screen of the same kind takes the freed slot",
      "browser_account.status_changed":
        "the person who opened a login screen on it (its owner, or an admin for them) is done while it needed them",
      "screen_ask.resolved": "the screen had an open needs-you ask (it ends with it)",
    },
  },
  "browserAccounts.create": { always: ["browser_account.created"] },
  "browserAccounts.revoke": {
    always: ["browser_account.revoked"],
    when: {
      "screen_lease.ended": "the account had live or waiting screens",
      "screen_lease.started": "a waiting screen takes a freed slot",
      "screen_ask.resolved": "one of those screens had an open needs-you ask",
    },
  },
  "browserAccounts.login": { always: [], oneOf: ["screen_lease.queued", "screen_lease.started"] },
  "browserAccounts.grant": { always: ["browser_account.granted"] },
  "browserAccounts.ungrant": { always: ["browser_account.grant_revoked"] },
  "browserAccounts.update": {
    always: [],
    oneOf: ["browser_account.updated"],
    noop: "the name and the note already read that way (nothing is written)",
  },
  "agentScreens.lease": {
    always: [],
    oneOf: ["screen_lease.queued", "screen_lease.started"],
    when: {
      "browser_account.created":
        "a `site` the company has no saved profile for: one is saved for the person the work is for",
      "browser_account.granted": "that new profile is lent to the agent (use_act)",
    },
    noop: "the run already holds or waits for this kind and account, or picks up its agent's screen that outlived an earlier run while waiting on a person (the same lease is returned)",
  },
  "agentScreens.needsYou": {
    always: [],
    oneOf: ["screen_lease.needs_you"],
    when: {
      "browser_account.status_changed": "a login or 2FA on a lease whose account is live",
      "message.created":
        "there is a place to post the card: where the run's trigger message or the task's origin message was said, else the task's thread, else the agent's DM with the person asked",
    },
    noop: "the lease is not active (queued, taken over, already waiting on a person, or ended), the agent already waits on a person for the same task on another screen, the last request's answer has not reached the agent yet (it gets that note instead), or a login ask is answered by reuse before asking (no saved profile, or its saved profile is checked first)",
  },
  "agentScreens.release": {
    always: [],
    oneOf: ["screen_lease.ended"],
    when: {
      "screen_lease.started": "a waiting screen of the same kind takes the freed slot",
    },
    noop: "the lease had already ended, or a person is on it (needs you, taken over): it is kept open",
  },
  // THREAD-READS
  "threads.read": {
    always: [],
    oneOf: ["thread.read"],
    noop: "the position would move backwards or stay (read positions never move back)",
  },
  "threads.readAll": {
    always: [],
    oneOf: ["thread.read"],
    noop: "no thread the caller follows has an unread reply",
  },
  "threads.follow": {
    always: [],
    oneOf: ["thread.follow_changed"],
    noop: "the caller already follows (or unfollowed) the thread as asked",
  },
  "threads.leaveAgent": {
    always: [],
    oneOf: ["thread.agent_left"],
    noop: "the agent had already left (or been muted in) the thread",
  },
  // AGENT-MENTION: ask again for one agent the message named.
  "messages.wake": {
    always: [],
    oneOf: ["wakeup.requested", "wakeup.dropped"],
    noop: "the agent's wake-up already stands (asked, started, or not started for the same reason)",
  },
  "messages.update": { always: ["message.edited"] },
  "messages.delete": {
    always: ["message.deleted"],
    when: { "attachment.removed": "the message had files (CHAT-FILES: each is trashed with it)" },
  },
  "messages.react": { always: ["reaction.added"] },
  "messages.unreact": { always: ["reaction.removed"] },
  "messages.pin": { always: ["message.pin_changed"] },
  "messages.makeTask": {
    always: [],
    oneOf: ["task.created"],
    when: {
      "page.created": "the message has text (M7-02: its body becomes the task's page)",
      // TASK-FILES (Work OS promoteThread): the message's files are copied onto the new task.
      "task.file_changed": "the message has files (each is copied onto the task)",
      // CHAT-FILES: a chat message's files are saved copies (provenance), audited like Save to task.
      "attachment.saved": "the message has chat files",
    },
    noop: "the `client_id` was already used (the first task is returned)",
  },
  "dms.open": {
    always: [],
    // CHAT-PRESENCE: opening an existing DM the caller had closed writes `channel.dm_reopened`
    // (and nothing else); a new DM writes `channel.created`.
    oneOf: ["channel.created", "channel.dm_reopened"],
    noop: "the DM of that member set already exists and the caller had not closed it",
    when: {
      "topic.created": "the DM is new (its `(no topic)` topic, where its messages go)",
    },
  },
  // CHAT-PRESENCE
  "dms.close": {
    always: [],
    oneOf: ["channel.dm_closed"],
    noop: "the DM was closed already and nobody has written since",
  },
  "dms.cleanup": {
    always: [],
    oneOf: ["channel.dm_closed"],
    noop: "no open DM of the caller is quiet that long (or each waits on the caller)",
  },
  "dePresence.ack": {
    always: [],
    oneOf: ["run.failure_acknowledged"],
    noop: "the DE is not stuck (no unacknowledged failure as its latest run)",
  },
  // CHAT-FILES
  "attachments.upload": { always: ["attachment.uploaded"] },
  "attachments.discard": { always: ["attachment.removed"] },
  "attachments.save": {
    always: ["task.file_changed", "attachment.saved"],
    when: { "task.file_folder_changed": FOLDER_CHAIN_WHEN },
  },
  "agentChatFiles.upload": {
    always: ["attachment.uploaded"],
    when: { "run.step_recorded": FILE_STEP_WHEN },
  },
  "agentChatFiles.save": {
    always: ["task.file_changed", "attachment.saved"],
    when: { "task.file_folder_changed": FOLDER_CHAIN_WHEN, "run.step_recorded": FILE_STEP_WHEN },
  },
  "follows.put": {
    always: [],
    oneOf: ["follow.changed"],
    noop: "the caller already follows the subject at that level",
  },
  "follows.delete": {
    always: [],
    oneOf: ["follow.changed"],
    noop: "the caller did not follow the subject",
  },
  "inbox.read": { always: ["inbox_item.read"] },
  "inbox.done": { always: ["inbox_item.done"] },
  "inbox.readAll": { always: ["inbox_item.read_all"] },
  "inbox.snooze": { always: ["inbox_item.snoozed"] },
  // M2-06 additions
  "channels.setPrefs": {
    always: [],
    oneOf: ["channel.prefs_changed"],
    when: {
      "channel.member_changed":
        "the caller joins, leaves, mutes or re-levels (M2-02's subscription row)",
      "follow.changed": "leaving drops the caller's follows of the channel and its topics",
    },
    noop: "the caller's settings already have those values",
  },
  "channels.setAgentRules": { always: ["channel.agent_rules_changed"] },
  // M2-01: boards and tasks
  "boards.create": {
    always: ["board.created", "channel.created", "view.created"],
    when: { "field_def.created": "the board template defines fields" },
  },
  "boards.update": { always: ["board.updated"] },
  "boards.archive": { always: ["board.archived", "channel.archived"] },
  "boards.unarchive": {
    always: ["board.unarchived"],
    when: { "channel.restored": "the board's home channel was archived with it" },
  },
  // BOARD-ORDER: a move always writes the target's new order (also when nothing moved).
  "boards.move": { always: ["board.moved"] },
  // T6-BOARD-NOTE
  "boards.writeNote": {
    always: [],
    oneOf: ["page.created", "page.edited"],
    when: {
      "board.updated": "the first write names the new page in `board.page_id`",
      // W1-FIXES: a person's edit of a DE-owned board's note wakes the DE to reconcile the rules
      "wakeup.requested": "a person changed the note of a board a DE owns (agents add-on on)",
    },
    noop: "the note already says that",
  },
  "boards.pauseRules": {
    always: [],
    // pausing writes `automation.paused` per rule not already paused; resuming,
    // `automation.updated` per rule "Pause rules" paused
    oneOf: ["automation.paused", "automation.updated"],
    noop: "no rule to pause or resume",
  },
  // T8-BOARD-NUMBERS
  "boards.addNumber": { always: ["metric_def.created"] },
  "boards.removeNumber": { always: ["metric_def.archived"] },
  "spaces.move": { always: ["space.moved"] },
  "views.create": { always: ["view.created"] },
  "views.update": { always: ["view.updated"] },
  "views.delete": { always: ["view.deleted"] },
  "fields.create": { always: ["field_def.created"] },
  "fields.update": { always: ["field_def.updated"] },
  "fields.delete": { always: ["field_def.deleted"] },
  "tasks.create": {
    always: [],
    oneOf: ["task.created"],
    when: {
      "page.created": "a body was given (M7-02: it becomes the task's page)",
      // COS-MODES
      "task.agent_directed":
        "Team mode: an agent created the task onto another person's agent (Tell me or Ask me first)",
      "approval.requested":
        "Team · Ask me first: that agent is left out and the request goes to its head",
      // B4-ITEM-TEMPLATES
      "task.tagged": "an item template with tags filled the task (one per tag)",
    },
    noop: "the `client_id` was already used (the first task is returned)",
  },
  // B4-ITEM-TEMPLATES: a board's item templates
  "itemTemplates.create": { always: ["item_template.created"] },
  "itemTemplates.update": {
    always: [],
    oneOf: ["item_template.updated"],
    noop: "nothing it names changes",
  },
  "itemTemplates.archive": { always: ["item_template.archived"] },
  // CLOSED-LOOPS: commitments
  "commitments.create": {
    always: ["commitment.created"],
    when: {
      "commitment.changed": "a decision under a live request: the request now waits on it",
      "inbox_item.created": "the owner is a person (asked in their Inbox)",
      "wakeup.requested": "the owner is an active DE (woken on the commitment)",
    },
  },
  "commitments.act": {
    always: ["commitment.changed"],
    when: {
      "commitment.created": "fail: the failure makes a decision owed (its options)",
      "inbox_item.created": "a person must act or hear next (asked, sent back, a decision)",
      "wakeup.requested": "an active DE must act next (sent back, a choice, a hand-over)",
    },
  },
  // BIZ-PLAYBOOKS / BIZ-CLIENT-HEALTH
  "pages.playbookSet": {
    always: [],
    oneOf: ["playbook.links_changed"],
    noop: "the links are already exactly the ones given",
  },
  "clients.surveyCreate": { always: ["client_survey.sent"] },
  "survey.answer": {
    always: ["client_survey.answered"],
    when: { "issue.raised": "the answer is a detractor's (NPS 0–6, CSAT 1–2)" },
  },
  "tasks.update": {
    always: [],
    // COS-MODES: an agent's priority or deadline change on another person's agent's task under
    // Ask me first files a request instead.
    oneOf: ["task.updated", "task.due_changed", "task.priority_changed", "approval.requested"],
    when: {
      "task.agent_directed":
        "Team mode: an agent changed the priority or deadline of another person's agent's task (Tell me or Ask me first)",
    },
  },
  "tasks.delete": {
    always: ["task.deleted"],
    when: { "page.archived": "the task had a body page (archived with it)" },
  },
  "tasks.move": {
    always: ["task.moved"],
    when: { "task.gate_held": "a DE moved it into a status its owner gates `hold` (B2-GATES)" },
  },
  "tasks.setStatus": {
    always: [],
    oneOf: ["task.status_changed"],
    when: { "task.gate_held": "a DE moved it into a status its owner gates `hold` (B2-GATES)" },
    noop: "the task already has that status",
  },
  // TASK-LOCK: each stopped run is cancelled as `runs.cancel` cancels one.
  "tasks.stopAgent": {
    always: [],
    oneOf: ["run.cancelled"],
    noop: "no agent run is working on the task",
    when: { "budget.settled": "an agent run was stopped (a started run settles its budget)" },
  },
  // CARD-ARCHIVE: archiving stops what works on the card; each part is conditional on there
  // being something to stop.
  "tasks.archive": {
    always: [],
    oneOf: ["task.archived"],
    noop: "the card is already archived",
    when: {
      "run.cancelled": "a member archived a card an agent run was working on",
      "run.failed":
        "an agency operator (the ops automation account) archived a card an agent run was working on (`agent_stopped`)",
      "budget.settled": "an agent run was stopped (a started run settles its budget)",
      "budget.released": "a queued agent run (not started) was stopped: its hold is released",
      "wakeup.dropped": "the card had queued wake-ups",
      "loop_incident.resolved": "a member archived a card with open loop incidents (closed)",
    },
  },
  "tasks.unarchive": {
    always: [],
    oneOf: ["task.unarchived"],
    noop: "the card is not archived",
  },
  "tasks.assign": {
    always: [],
    // COS-MODES: an agent's assignment of another person's agent under Ask me first files a
    // request instead (`approval.requested`); nothing is assigned yet.
    oneOf: ["task.assigned", "approval.requested"],
    when: {
      "task.unassigned": "a handoff: the handing seat leaves the assignees",
      "task.handed_off": "a handoff (HANDOFF-LOG): who handed the task to whom, and why",
      // COS-SLICE1: the third agent handoff in 24 h parks the task and tells a person.
      "task.handoff_parked": "an agent's handoff is the third by agents in 24 h",
      "inbox_item.created": "the task was parked: its holder's supervisor chain is told",
      "task.agent_directed":
        "Team mode: an agent directed another person's agent under Tell me or Ask me first (the head is told, or asked)",
    },
    noop: "every seat was already assigned",
  },
  // COS-MODES
  "chiefOfStaff.setMode": {
    always: [],
    oneOf: ["company.updated"],
    when: {
      "chief_of_staff.mode_changed": "the mode changes (Solo ↔ Team): its heads are told once",
      "approval.expired": "Team → Solo with Ask-me-first requests waiting: each closes",
      "task.assigned": "Team → Solo: a waiting hand-to-agent request proceeds",
      "task.unassigned":
        "Team → Solo: a waiting handoff request proceeds (the handing seat leaves)",
      "task.handed_off": "Team → Solo: a waiting handoff request proceeds",
      "task.priority_changed": "Team → Solo: a waiting priority request proceeds",
      "task.due_changed": "Team → Solo: a waiting deadline request proceeds",
      "task.agent_directed": "Team → Solo: each request that proceeds is recorded for its head",
      "message.created":
        "Team → Solo: a waiting new brief to an agent already on the task proceeds (posted on the card)",
      "wakeup.requested":
        "Team → Solo: a waiting new brief to an agent already on the task proceeds (the agent is woken)",
    },
    noop: "the body equals what is stored",
  },
  "agentDirection.put": {
    always: [],
    oneOf: ["membership.updated"],
    noop: "the body equals what is stored",
  },
  "tasks.takeBack": {
    always: ["task.taken_back"],
    when: {
      "task.assigned": "an assignment taken back: the head's seat is assigned",
      "task.unassigned": "an assignment taken back: the agent's seat leaves the assignees",
      "task.handed_off": "an assignment taken back: one handoff with the route line",
      "task.priority_changed": "a priority change taken back: the priority before is restored",
      "task.due_changed": "a deadline change taken back: the deadline before is restored",
      "run.cancelled": "the agent was working on the task: its run is stopped",
      // As `tasks.stopAgent`: a started run that is stopped settles its budget.
      "budget.settled": "the agent was working on the task (a started run settles its budget)",
      "message.created": "the card gets the comment 'taken back by …'",
    },
  },
  "tasks.unassign": { always: ["task.unassigned"] },
  "tasks.setField": {
    always: [],
    oneOf: ["task.field_changed"],
    noop: "the field already has that value",
  },
  "tasks.addChecklistItem": { always: ["task.checklist_changed"] },
  "tasks.updateChecklistItem": { always: ["task.checklist_changed"] },
  "tasks.deleteChecklistItem": { always: ["task.checklist_changed"] },
  // DOCS-KB: named checklists
  "tasks.createChecklist": { always: ["task.checklist_changed"] },
  "tasks.updateChecklist": {
    always: [],
    oneOf: ["task.checklist_changed"],
    noop: "nothing changed (the checklist is returned as it is)",
  },
  "tasks.deleteChecklist": { always: ["task.checklist_changed"] },
  "tasks.reorderChecklist": {
    always: [],
    oneOf: ["task.checklist_changed"],
    noop: "the items were already in that order",
  },
  "tasks.addDependency": { always: ["task.dependency_changed"] },
  "tasks.removeDependency": { always: ["task.dependency_changed"] },
  "tasks.bulk": {
    always: [],
    oneOf: [
      "task.status_changed",
      "task.assigned",
      "task.moved",
      "task.field_changed",
      "task.deleted",
      "task.tagged",
      "task.untagged",
    ],
    when: {
      "page.archived": "a deleted task had a body page (archived with it)",
      "task.gate_held": "a DE set a status its owner gates `hold` (B2-GATES)",
    },
    noop: "no task accepted the action (every per-task result failed or was unchanged)",
  },
  // M2-03
  "tasks.propose": { always: ["approval.requested"] },
  // B2-GATES
  "boards.putGate": { always: ["board.gates_changed"] },
  "boards.deleteGate": { always: ["board.gates_changed"] },
  "boards.gateCeiling": {
    always: [],
    oneOf: ["board.gates_changed"],
    noop: "the ceiling already says that",
  },
  "boards.proposeGates": { always: ["approval.requested"] },
  "boards.batchReview": { always: ["approval.requested"] },
  // BOARD-GATES: the decision moves the task; the note of a rework or reject is posted to it
  "tasks.review": {
    always: ["task.status_changed"],
    when: {
      "message.created": "a rework or reject (its note is posted to the task)",
      "task.updated": "an approve that edits the title first",
      "task.field_changed": "an approve that edits fields first",
    },
  },
  "tasks.pullHold": {
    always: ["task.gate_pulled", "message.created"],
    // B2-FIXES: back to its status on the same board, or back to the board it came from
    oneOf: ["task.status_changed", "task.moved"],
    when: {
      "wakeup.requested": "the DE that moved it is active (it is woken with the note)",
      "inbox_item.created": "the DE that moved it is active (the note is its inbox item)",
    },
  },
  "approvals.sendBack": {
    always: [],
    oneOf: ["approval.decided"],
    when: { "message.created": "the approval is about a task: the note is posted to it" },
    noop: "the approval was already decided (the first decision is returned)",
  },
  // IMPORT (HARDEN)
  "importRuns.create": { always: ["import_run.created"] },
  "importRuns.updateMapping": { always: ["import_run.mapping_updated"] },
  "importRuns.start": { always: ["import_run.queued"] },
  "importRuns.cancel": {
    always: [],
    oneOf: ["import_run.cancelled", "import_run.cancel_requested"],
  },
  // M2-08
  "tasks.import": {
    always: ["import.committed"],
    when: { "task.created": "the file has rows that are not yet on the board" },
  },
  // M7-02: pages
  "pages.create": {
    always: ["page.created"],
    // MULTI-SPACE-ACCESS
    when: { "page.shares_changed": "`shares` names a space to share it into" },
  },
  "pages.shares.set": {
    always: [],
    oneOf: ["page.shares_changed"],
    noop: "the shares the caller may change are already the ones named",
  },
  "pages.members.set": {
    always: [],
    oneOf: ["page.members_changed"],
    when: { "access.changed": "each person or seat given, changed or removed (one per grant)" },
    noop: "the people and seats named already have those levels",
  },
  "pages.update": {
    always: [],
    oneOf: ["page.edited"],
    noop: "nothing changed (the page is returned as it is)",
  },
  "pages.move": {
    always: [],
    oneOf: ["page.moved"],
    noop: "the page is already at that place",
  },
  "pages.archive": {
    always: [],
    oneOf: ["page.archived"],
    noop: "the page was already archived",
  },
  "pages.restore": { always: ["page.edited"] },
  "tasks.page": {
    always: [],
    oneOf: ["page.created"],
    noop: "the task already has its page",
  },
  // DOCS-KB
  "pages.unarchive": {
    always: [],
    oneOf: ["page.unarchived"],
    noop: "the page was not archived",
  },
  "pages.delete": { always: ["page.deleted"] },
  "pages.undelete": { always: ["page.undeleted"] },
  // KB-PDF-UPLOAD
  "pages.upload": {
    // UPLOAD-PRIVATE-FIRST: the text is queued to be read in the background.
    always: ["page.created", "page.upload_queued"],
    // KB-UPLOAD-2: "Add as" Reference (read only) and "Share with".
    when: {
      "page.edited": "`read_only` is `true`",
      "page.shares_changed": "`shares` names a space to share it into",
      "space.created": "no `space_id` and the uploader had no personal space yet",
    },
  },
  "pages.uploadRetry": { always: ["page.upload_queued"] },
  // UPLOAD-ORIGINAL: the original file managed from the doc.
  "pages.uploadReplace": { always: ["page.upload_changed", "page.upload_queued"] },
  "pages.uploadReextract": { always: ["page.upload_queued"] },
  "pages.uploadRemoveOriginal": { always: ["page.upload_changed"] },
  "pages.uploadDetach": { always: ["page.upload_changed"] },
  "agentKnowledge.add": {
    always: [],
    oneOf: ["agent.knowledge_changed"],
    noop: "every source was already in the knowledge base",
  },
  "agentKnowledge.remove": { always: ["agent.knowledge_changed"] },
  // M3-01: approvals, runs, wake-ups, delegation, a DE's agents and budget, key rotation
  "approvals.create": { always: ["approval.requested"] },
  "approvals.approve": {
    always: [],
    oneOf: ["approval.decided"],
    when: {
      "budget.overrun_approved": "the approval is a `budget_overrun` (applied in the decision)",
      "budget.changed": "the approval is a `budget_overrun`: its cap is raised",
      "digital_employee.resumed":
        "a `budget_overrun` whose requesting DE was paused for the budget",
      // AGENT-REPORTING (13 §Reporting duties): a reviewed agent value is recorded on decision
      "observation.recorded": "a `kpi_value` of a value metric: the reviewed value is recorded",
      "outcome.recorded": "a `kpi_value` of an outcome event metric: its items are ingested",
      "activity.recorded": "a `kpi_value` of an activity event metric: its items are ingested",
    },
    noop: "the approval was already decided (the first decision is returned)",
  },
  "approvals.reject": {
    always: [],
    oneOf: ["approval.decided"],
    noop: "the approval was already decided (the first decision is returned)",
  },
  // CORE-CARDS: a card option decides as approve or reject (the option says which); the kinds
  // applied inside the decision (`budget_overrun`, `kpi_value`) carry no card
  "approvals.choose": {
    always: [],
    oneOf: ["approval.decided"],
    noop: "the approval was already decided (the first decision is returned)",
  },
  "approvals.edit": {
    always: [],
    oneOf: ["approval.decided"],
    when: {
      "budget.overrun_approved": "the approval is a `budget_overrun` (applied in the decision)",
      "budget.changed": "the approval is a `budget_overrun`: its cap is raised",
      "digital_employee.resumed":
        "a `budget_overrun` whose requesting DE was paused for the budget",
      // AGENT-REPORTING (13 §Reporting duties): a reviewed agent value is recorded on decision
      "observation.recorded": "a `kpi_value` of a value metric: the reviewed value is recorded",
      "outcome.recorded": "a `kpi_value` of an outcome event metric: its items are ingested",
      "activity.recorded": "a `kpi_value` of an activity event metric: its items are ingested",
    },
    noop: "the approval was already decided (the first decision is returned)",
  },
  "runs.create": {
    always: ["run.queued", "budget.reserved"],
    when: { "wakeup.coalesced": "`wakeup_ids` names queued wake-ups" },
  },
  // AGENT-KPI-TOOL: every record shows in the run's timeline (one step, a duplicate too); the
  // KPI write happens once per (metric, period, run): a value or the event of an event metric.
  // T3-TASK-ROLLUPS: a DE's task metric waits for a company admin.
  "kpis.proposeTaskMetric": { always: ["approval.requested"] },
  "outcomes.record": {
    always: ["run.step_recorded"],
    when: {
      "observation.recorded": "a value the run had not recorded for the metric's period",
      "outcome.recorded":
        "an event of an outcome metric the run had not recorded for the metric's period",
      "activity.recorded":
        "an event of an activity metric the run had not recorded for the metric's period",
      // AGENT-REPORTING: a `needs_review` metric's record is held for a person instead
      "approval.requested":
        "the metric needs review: the value waits as a `kpi_value` approval (nothing is recorded)",
    },
  },
  // D15 (preview): every accepted write is a step; a lesson waits for a person
  "memory.agentRemember": {
    always: ["run.step_recorded"],
    when: {
      "approval.requested":
        "the write is a lesson: it waits as a `memory_lesson` approval (nothing reaches memory)",
    },
  },
  // RECALL-RULES / RECALL-PROCEDURES
  "memoryRules.create": {
    always: ["memory_rule.changed"],
    when: {
      "approval.requested":
        "the caller is not an owner or admin: the rule is proposed as a `memory_rule` approval",
    },
  },
  "memoryRules.lift": {
    always: [],
    oneOf: ["memory_rule.changed"],
    noop: "the rule was already lifted",
  },
  "memory.agentSuggestRule": {
    always: ["memory_rule.changed", "approval.requested", "run.step_recorded"],
  },
  "memory.agentProcedureRun": { always: ["procedure.run_recorded", "run.step_recorded"] },
  // SKILL-PROPOSALS-OPERATOR: the proposal itself goes to the operator-only queue (no company
  // event); the run's step is all the company's log holds.
  "skillProposals.agentPropose": { always: ["run.step_recorded"] },
  // RECALL-RUN-LIFECYCLE: each is a step of the run (what memory handed it; the carry note)
  "memory.agentWake": { always: ["run.step_recorded"] },
  "memory.agentCarry": { always: ["run.step_recorded"] },
  // AGENT-REPORTING (13 §Reporting duties)
  "reporting.putDuty": { always: ["reporting_duty.changed"] },
  "reporting.deleteDuty": {
    always: [],
    oneOf: ["reporting_duty.changed"],
    noop: "the seat measurable had no duty",
  },
  "seats.putKpiScopes": {
    always: [],
    oneOf: ["seat.updated"],
    noop: "the seat's KPI scopes are already what was asked",
  },
  "runs.appendSteps": {
    always: ["run.step_recorded"],
    when: {
      "run.started": "the run was `queued` (the first batch starts it)",
      ...LOOP_WHEN,
    },
  },
  // LOOP-BREAKER: the turns are booked as steps; the verdict may warn once, or stop the run.
  "runs.reportTurns": {
    always: [],
    // A turn reported, or the run warned or stopped (its timeline note).
    oneOf: ["run.step_recorded"],
    when: {
      "run.started": "the run was `queued` (the first batch starts it)",
      ...LOOP_WHEN,
    },
    noop: "no turn was reported and the runtime's guard did not stop the run",
  },
  "loopIncidents.resolve": {
    always: [],
    oneOf: ["loop_incident.resolved"],
    when: {
      "message.created": "retry of a task's run: the hint is a note in the task's conversation",
      "inbox_item.created": "retry of a run without a task: the agent gets the hint in its inbox",
      "wakeup.requested": "retry: the agent is woken on its task (or the inbox item) with the hint",
    },
    noop: "the incident was already resolved with the same action",
  },
  "loopBreaker.settings.update": {
    always: [],
    oneOf: ["company.updated"],
    noop: "the override is already as asked",
  },
  "operator.loopBreaker.set": {
    always: [],
    oneOf: ["loop_breaker.default_changed"],
    noop: "the platform default is already as asked",
  },
  "runs.wait": { always: ["run.waiting"] },
  "runs.complete": { always: ["run.completed", "budget.settled"] },
  "runs.fail": { always: ["run.failed", "budget.settled"] },
  "runs.cancel": {
    always: ["run.cancelled"],
    oneOf: ["budget.settled", "budget.released"],
  },
  // FAILURE-STOP: clears the run's error counters and stop.
  "runs.continueAfterFailures": {
    always: ["run.failures_continued"],
    when: {
      "wakeup.requested": "the stopped run had ended: its DE is woken on the run's task again",
    },
  },
  "runs.delegate": {
    always: ["delegation_request.sent", "run.delegated"],
    when: { "wakeup.requested": "the recipient is an agent" },
  },
  // DC-08: an admitted worker is a queued child run with its reservation (a refusal writes its
  // `run.worker_refused` after the rollback: `backgroundProducers["admission:worker_refusal"]`).
  "runs.spawnWorker": { always: ["run.queued", "budget.reserved"] },
  "runs.sandbox": { always: [], oneOf: ["sandbox.started", "sandbox.stopped"] },
  // AGENT-PLUMBING: booked within the run's room and the auto-book limit, else filed for a person.
  "runs.reportCost": { always: [], oneOf: ["cost.posted", "approval.requested"] },
  "runs.wakeAfter": { always: ["wakeup.scheduled"] },
  // B5-DECIDE: every call is an Activity line; its cost is booked when the provider priced it.
  "runs.decide": {
    always: ["classifier.decided"],
    when: { "cost.posted": "the call cost more than zero (Jev's input tokens at its price)" },
  },
  "runs.decisionRubrics.save": { always: ["decision_rubric.saved"] },
  "decisionRubrics.edit": { always: ["decision_rubric.saved"] },
  "decide.settings.update": {
    always: [],
    oneOf: ["company.updated"],
    noop: "nothing changed (the switches already had those values)",
  },
  // DE-QUICK-ACK: the model call is always booked (a step tagged `quick_reply`); the reply is
  // posted only when the answer says something and every guard passes.
  "runs.quickReply": {
    always: ["run.step_recorded"],
    when: {
      "run.started": "the run was `queued` (the quick reply came before the runtime's first step)",
      "message.created":
        "the model's answer is an acknowledgement or a question and every guard passes (a person's message of this run, quick replies on, none sent yet, no post of the DE there in the last 20 s)",
    },
  },
  "wakeups.create": {
    always: [],
    oneOf: ["wakeup.requested"],
    noop: "the `idempotency_key` was already used for this agent (the first request is returned)",
  },
  // M3-06: the runtime's poll claim runs the API's own admission over queued wake-ups.
  "wakeups.dispatch": {
    always: [],
    // A group is either admitted (run.queued), dropped (wakeup.dropped), or — PAUSE-NO-DROP — held
    // on a budget in the chain (budget.reservation_refused; the wake-ups stay queued, held).
    oneOf: ["run.queued", "wakeup.dropped", "budget.reservation_refused"],
    when: {
      "budget.reserved": "a group of queued wake-ups was admitted into a new run",
      "wakeup.coalesced": "a group of queued wake-ups was admitted into a new run",
      // Phase 2 increment 6: a shape-A run without routing evidence fails `profile_unroutable`.
      "run.failed": "an admitted shape-A run had no routing evidence (`profile_unroutable`)",
      "budget.settled": "an admitted shape-A run had no routing evidence (`profile_unroutable`)",
    },
    noop: "no queued wake-up could be admitted or dropped (none, or all deferred); queued runs are still returned",
  },
  "delegationRequests.accept": {
    always: ["delegation_request.accepted"],
    when: { "wakeup.requested": "the recipient is an agent" },
  },
  "delegationRequests.decline": {
    always: ["delegation_request.declined"],
    when: { "wakeup.requested": "the requester is an agent" },
  },
  "delegationRequests.complete": {
    always: ["delegation_request.completed"],
    when: { "wakeup.requested": "the requester is an agent" },
  },
  "digitalEmployees.addAgent": { always: ["agent_profile.created"] },
  "digitalEmployees.updateAgent": { always: ["agent_profile.updated"] },
  "digitalEmployees.retireAgent": { always: ["agent_profile.retired"] },
  "digitalEmployees.updateBudget": {
    always: ["budget.changed"],
    when: {
      // BUDGET-CAP (finance/budget-resume.ts)
      "digital_employee.resumed": "the DE was paused for budget and the change gave it room",
    },
  },
  "companies.updateBudgetPace": { always: ["company.updated"] },
  "agentKeys.rotate": { always: ["agent_key.rotated", "agent_key.created"] },
  // M4-01: personal crews (the owner's own) and their ceiling (admins)
  "crews.update": {
    always: [],
    oneOf: ["personal_crew.updated", "membership.updated"],
    noop: "nothing changed",
  },
  "crews.putAutonomy": { always: ["autonomy_policy.changed"] },
  "crews.putSharing": { always: ["sharing_policy.changed"] },
  "crews.addAgent": { always: ["crew_agent.added"] },
  "crews.removeAgent": { always: ["crew_agent.removed"] },
  "crews.answerOnboarding": {
    always: ["personal_crew.onboarding_answered"],
    when: { "crew_context.updated": "`complete` writes answers into context layers" },
  },
  "crews.putContextLayer": { always: ["crew_context.updated"] },
  "crews.pause": {
    always: [],
    oneOf: ["personal_crew.paused"],
    noop: "the crew was already paused",
  },
  "crews.resume": {
    always: [],
    oneOf: ["personal_crew.resumed"],
    noop: "the crew was already active",
  },
  "roleSkillPolicies.put": { always: ["role_skill_policy.changed"] },
  "roleSkillPolicies.delete": {
    always: [],
    oneOf: ["role_skill_policy.changed"],
    noop: "the role had no policy",
  },
  "crewCaps.put": { always: ["crew_cap.changed"] },
  // M4-05: agent rooms (participants, turns) and status asks
  "agentRooms.create": {
    always: ["agent_room.created"],
    when: {
      "channel.created": "a new room channel is made in a space (`space_id` + `name`)",
      "channel.member_changed":
        "a new room channel gets its creator as admin, or an agent participant joins the channel",
      "channel.updated": "an existing channel becomes a room (`channel_id`; it was not flagged)",
      "topic.created": "a channel room had no topic yet: it gets one named Room",
      "agent_room.participant_changed": "`participant_ids` names someone",
      "agent_room.turn_changed": "an agent participant gives the room its first turn holder",
    },
  },
  "agentRooms.update": { always: ["agent_room.updated"] },
  "agentRooms.delete": {
    always: ["agent_room.removed"],
    when: {
      "channel.archived": "the room is a room channel of its own (it is archived)",
      "channel.updated":
        "the room is a channel flagged a room with no topic room left on it (unflagged)",
    },
  },
  "agentRooms.addParticipant": {
    always: [],
    oneOf: ["agent_room.participant_changed"],
    noop: "the membership already takes part",
    when: {
      "channel.member_changed": "an agent joins a channel it was not a member of",
      "agent_room.turn_changed": "the first agent gives the room its turn holder",
    },
  },
  "agentRooms.removeParticipant": {
    always: ["agent_room.participant_changed"],
    when: {
      "channel.member_changed": "an agent leaves the channel with the room",
      "agent_room.turn_changed": "the removed agent held the turn",
      "wakeup.requested": "the turn passes to another agent",
    },
  },
  "agentRooms.join": {
    always: [],
    oneOf: ["agent_room.participant_changed"],
    noop: "the caller already takes part",
  },
  "agentRooms.leave": { always: ["agent_room.participant_changed"] },
  "agentRooms.pass": {
    always: ["agent_room.turn_changed"],
    when: { "wakeup.requested": "the turn passes to an agent (the room does not stall)" },
  },
  "statusAsks.create": {
    always: ["status_ask.created", "message.created"],
    when: {
      "wakeup.requested": "the target's sharing policy lets the ask through (it is delivered)",
      "status_ask.escalated": "the sharing policy refuses it: it goes to the target's human",
    },
  },
  "statusAsks.answer": {
    always: ["status_ask.answered", "message.created"],
    when: { "wakeup.requested": "the asker is an agent still in the room" },
  },
  "statusAsks.decline": {
    always: ["status_ask.declined", "message.created"],
    when: { "wakeup.requested": "the asker is an agent still in the room" },
  },
  // M4-03: crew features (the owner's settings and decisions; the crew run's proposals)
  "crews.putFeatures": { always: ["personal_crew.features_changed"] },
  "crews.brief": {
    always: [],
    oneOf: ["crew_brief.posted"],
    when: {
      "message.created": "the day has no brief yet: the crew posts it",
      "topic.created": 'the day has no brief yet: its topic "Brief {date}" is new',
    },
    noop: "today's brief was already posted (it is returned)",
  },
  "crews.acceptProposal": {
    always: ["crew_proposal.decided"],
    when: {
      "inbox_item.done": "a `done` proposal whose item is still open",
      "inbox_item.snoozed": "a `snooze` proposal whose item is still open",
      "approval.decided": "a `reply`: its approval is approved (the executor posts the draft)",
      "reminder.created": "a `remind` proposal",
      "task.status_changed": "a `task_update` with a status the task does not have",
      "message.created": "a `task_update` with a comment (posted as the crew in the task)",
    },
  },
  "crews.dismissProposal": {
    always: ["crew_proposal.decided"],
    when: { "approval.decided": "a `reply`: its approval is rejected" },
  },
  "crews.addReminder": { always: ["reminder.created"] },
  "crews.updateReminder": {
    always: [],
    oneOf: ["reminder.updated"],
    noop: "the text and time given are the saved ones",
  },
  "crews.dismissReminder": {
    always: [],
    oneOf: ["reminder.dismissed"],
    noop: "the reminder was already dismissed (it is returned)",
  },
  "crews.putMemoryGrants": { always: ["crew_memory_grant.changed"] },
  // D12: the personal read grant, once Recall answered it
  "crews.putPersonalMemory": { always: ["recall.personal_grant_changed"] },
  // M7-05: the seat and the DE as `POST seats` and `POST digital-employees` write them, then
  // the hire.
  "crews.hireAsDe": {
    always: [
      "seat.created",
      "membership.created",
      "digital_employee.created",
      "seat.assigned",
      "access.granted",
      "crew.hired_as_de",
      // AGENT-RECORD: every template seeds a metric set with default targets.
      "seat_metric.changed",
      "target_rule.changed",
    ],
    // The seat is new, so it sits in no team yet: the assignment never changes a composition.
    when: {
      "metric_def.created":
        "the company predates a catalog entry of the metric set (written first)",
    },
  },
  "crewRun.propose": {
    always: ["crew_proposal.created"],
    when: {
      "approval.requested": "a `reply` that waits for the owner (`ask`): its approval",
      "inbox_item.done": "a `done` applied alone",
      "inbox_item.snoozed": "a `snooze` applied alone",
      "message.created": "a `reply` applied alone, or a `task_update` comment applied alone",
      "reminder.created": "a `remind` applied alone",
      "task.status_changed": "a `task_update` status applied alone",
    },
  },
  // M3-01: finance controls
  "budgets.create": { always: ["budget.changed"] },
  "budgets.update": {
    always: ["budget.changed"],
    when: {
      // BUDGET-CAP (finance/budget-resume.ts)
      "digital_employee.resumed": "a DE under the budget was paused for budget and now has room",
    },
  },
  "budgets.archive": { always: ["budget.changed"] },
  "budgets.unarchive": { always: ["budget.changed"] },
  "priceLists.create": { always: ["price_list.changed"] },
  // HTVA-RUN-FIXES: in the platform company's log (the cells get the row by mirror).
  "operator.costPrices.create": { always: ["billing.price_changed"] },
  "fxRates.put": { always: ["fx_rate.set"] },
  // M5-03: finance views.
  "financeAccess.grant": {
    always: [],
    oneOf: ["access.granted"],
    noop: "the person already holds the relation",
  },
  "financeAccess.revoke": {
    always: [],
    oneOf: ["access.revoked"],
    noop: "the person did not hold the relation",
  },
  "categories.create": { always: ["category.changed"] },
  "categories.update": { always: ["category.changed"] },
  "clients.create": { always: ["client.changed"] },
  "clients.update": { always: ["client.changed"] },
  "revenueEvents.create": { always: ["revenue.posted"] },
  "compensation.create": { always: ["compensation.changed"] },
  "compensation.update": { always: ["compensation.changed"] },
  "recurringExpenses.create": { always: ["recurring_expense.changed"] },
  "recurringExpenses.update": { always: ["recurring_expense.changed"] },
  "scenarios.create": { always: ["scenario.created", "scenario.evaluated"] },
  "scenarios.evaluate": { always: ["scenario.evaluated"] },
  "scenarios.update": { always: ["scenario.updated", "scenario.evaluated"] },
  "scenarios.delete": { always: ["scenario.deleted"] },
  "headcountPlan.create": { always: ["headcount_plan.changed"] },
  "headcountPlan.update": { always: ["headcount_plan.changed"] },
  "finance.export": { always: ["finance.exported"] },
  // EXPORT-CORE: asked in the request, completed once the archive passed the scan.
  "companies.export": { always: ["company.export_requested", "company.export_completed"] },
  "sourceKeys.create": { always: ["agent_key.created"] },
  "sourceKeys.revoke": { always: ["agent_key.revoked"] },
  // M4-02: skill packs (role assignment; the content is immutable)
  // WORKFORCE
  "workforceAgents.create": { always: ["workforce_agent.registered"] },
  // PURGE-RETIRED-AGENTS: a retired AG removed for good.
  "workforceAgents.purge": { always: ["agent.purged"] },
  // CONTENT-STUDIO-LINK: the line and its time are set on every report.
  "workforceAgents.reportActivity": {
    always: [],
    oneOf: ["workforce_agent.updated"],
    noop: "the same line again within the same millisecond (the agent is returned)",
  },
  // MANAGED-POLICY
  "agentMode.put": {
    always: [],
    oneOf: ["company.agent_mode_changed"],
    noop: "the company is already in the mode asked",
  },
  "agentRequests.create": { always: ["agent_request.created"] },
  "agentRequests.resolve": { always: ["agent_request.resolved"] },
  // ONBOARDING-P3: the setup assistant's post in the client's support channel (a system message).
  "onboarding.present": {
    always: [],
    oneOf: ["message.created"],
    when: { "topic.created": "the channel's main topic did not exist yet" },
    noop: "that version's proposal (or its call offer) is already in the channel",
  },
  // ONBOARDING-P3: one step of one seat of the seating run. A person's seat is opened; a DE seat
  // gets its seat and its DE from its template (digitalEmployees.create's events), then its set-up
  // is asked for (again after each ten-minute wait), it is connected, then activated.
  "onboarding.seating.next": {
    always: [],
    oneOf: ["seat.created", "digital_employee.provision_requested", "digital_employee.activated"],
    when: {
      "role.created": "the company has no role named as the seat yet",
      "membership.created": "a DE seat",
      "digital_employee.created": "a DE seat",
      "seat.assigned": "a DE seat",
      "access.granted": "a DE seat",
      "seat_metric.changed": "a DE seat whose template carries a metric set",
      "target_rule.changed": "a DE seat whose template carries a metric set",
      // No `team.updated`: the runner opens the DE's seat in the same step (seating.ts
      // createStep), and a seat that has just been opened is in no team, so assigning the DE
      // never changes a team's composition here.
      "metric_def.created": "the company predates a catalog entry the DE's seat needs",
    },
    noop: "nothing due, a person's seat that already exists, a DE whose set-up is done (marked set up), whose runtime connection is waited on or found, or a seat that became a template ticket, was refused (allowance, no approval) or is blocked (no person to report to)",
  },
  // HELPERS: paid helper requests
  "agentRequests.approve": {
    always: ["agent_request.stage_changed"],
    // Wave 23: approving here is Billing's Approve of the quote's helper offer.
    when: { "helper_offer.changed": "the quote is a live, open helper offer" },
  },
  "agentRequests.stage": {
    always: ["agent_request.stage_changed"],
    when: {
      "agent_request.resolved": "the stage is added or declined (the request closes)",
      // Wave 23: a quote is a new helper offer; a live offer follows the request to added/declined.
      "helper_offer.changed": "the stage is quoted, or the request's live offer follows it",
    },
  },
  "workforceAgents.update": {
    always: [],
    oneOf: ["workforce_agent.updated"],
    noop: "every field sent equals the agent's (the agent is returned)",
  },
  // HELPERS-2: a DE's use of an agent it does not lead.
  "workforceAgents.addUse": {
    always: [],
    oneOf: ["workforce_agent.use_changed"],
    noop: "the DE uses it already (the agent is returned)",
  },
  "workforceAgents.removeUse": {
    always: [],
    oneOf: ["workforce_agent.use_changed"],
    noop: "the DE does not use it (the agent is returned)",
  },
  "workforceAgents.declareUse": {
    always: [],
    oneOf: ["workforce_agent.use_changed"],
    noop: "already so, or the DE leads the agent (its home: nothing stored)",
  },
  // WF-CONTROL
  "control.tokens.create": { always: ["control_token.created"] },
  "control.tokens.revoke": {
    always: [],
    oneOf: ["control_token.revoked"],
    noop: "the connection was already revoked (it is returned)",
  },
  "control.pair": { always: ["control_token.paired"] },
  "workforcePlugin.put": {
    always: [],
    oneOf: ["company.entitlement_changed"],
    noop: "the add-on is already as asked",
  },
  "skillPacks.update": {
    always: [],
    oneOf: ["skill_pack.roles_changed", "skill_pack.archived", "skill_pack.restored"],
    noop: "the role set and the archived state equal the pack's (the pack is returned)",
  },
  // SKILL-OVERRIDES
  "skills.additions.put": {
    always: [],
    oneOf: ["skill_pack.additions_changed"],
    noop: "the same additions again with no newer default to review, or a reset with none (the skill is returned)",
  },
  // DC-01: DE configuration (ADR-0018, ADR-0019, ADR-0020 widened)
  "operator.aigentpanel.launch": { always: ["launch_token.minted"] },
  "aigentpanel.callbacks": {
    always: [],
    oneOf: [
      "deploy.confirmed",
      "deploy.failed",
      "launch_token.used",
      "digital_employee.panel_drift",
      // SCREENS-S1
      "screen_stack.registered",
      "screen_stack.health_changed",
      // PROVISION-IN-PRODUCT: the wake intake
      "digital_employee.bridge_registered",
      "digital_employee.bridge_removed",
    ],
    when: {
      "screen_lease.ended":
        "a `stack.registered` / `stack.health` retires the stack while it has live or waiting screens",
      "screen_ask.resolved": "one of those screens had an open needs-you ask (it ends with it)",
    },
    noop: "the delivery was already applied for `(deploy_id, type)` (`duplicate: true`), or it is kept until the deploy's binding (`pending: true`); a `stack.health` that changes neither health nor state, or is older than the last one; a `bridge.removed` for a DE with no bridge registered",
  },
  "operator.agentTemplates.create": { always: ["agent_template.created"] },
  "operator.templateVersions.create": { always: ["agent_template_version.drafted"] },
  "operator.templateVersions.update": { always: ["agent_template_version.draft_updated"] },
  "operator.templateVersions.validate": {
    always: [],
    oneOf: ["agent_template_version.validated", "agent_template_version.validation_failed"],
  },
  "operator.templateVersions.publish": { always: ["agent_template_version.published"] },
  "operator.templateVersions.deprecate": { always: ["agent_template_version.deprecated"] },
  "operator.templateEditProposals.create": { always: ["template_edit.proposed"] },
  "operator.templateEditProposals.accept": {
    always: ["template_edit.accepted", "agent_template_version.drafted"],
  },
  "operator.templateEditProposals.reject": { always: ["template_edit.rejected"] },
  "operator.deploys.stage": { always: ["deploy.staged"] },
  "operator.deploys.approve": { always: ["deploy.approved"] },
  "operator.deploys.reject": { always: ["deploy.rejected"] },
  "deploys.approve": { always: ["deploy.approved"] },
  "deploys.reject": { always: ["deploy.rejected"] },
  "digitalEmployees.putTemplateOverride": { always: ["de_template_override.changed"] },
  // TASK-BUDGETS: an override equal to the stored one writes nothing.
  "digitalEmployees.putTaskLimits": {
    always: [],
    oneOf: ["digital_employee.updated"],
    noop: "the override is already what is stored",
  },
  // CHART-HOWIWORK: a save of the text already current writes no version and emits nothing.
  "digitalEmployees.putHowIWork": {
    always: [],
    oneOf: ["digital_employee.updated"],
    noop: "the text is already the current version",
  },
  // DE-WIZARD: a save equal to what is stored writes nothing. Otherwise: job_changed when the
  // path, submit, home channel or reporting changes; seat_metric.changed when a KPI is added,
  // changed or removed on the DE's seat; target_rule.changed when a KPI's weekly target is set or
  // changed; seat.updated when the seat's post approval (its settings) changes;
  // reporting_duty.changed when a KPI joins or leaves the agent's reporting (AGENT-REPORTING:
  // the seat's agent_tool duty of that metric is set or removed).
  "digitalEmployees.putJob": {
    always: [],
    oneOf: [
      "digital_employee.job_changed",
      "seat_metric.changed",
      "target_rule.changed",
      "seat.updated",
      "reporting_duty.changed",
    ],
    noop: "the body changes nothing the DE's job, its seat's KPIs or its post approval hold",
  },
  // D10 scope editor: modes equal to the seat's stored ones change nothing and write nothing.
  "seats.agentPolicyUpdate": {
    always: [],
    oneOf: ["agent_policy.changed"],
    noop: "every mode named equals the seat's stored one",
  },
  // REACH-S1: an edit equal to the reach in force writes nothing; an ask already pending is
  // returned as it is; a grant of a person already in reach (added meanwhile) changes no reach.
  "agentReach.update": {
    always: [],
    oneOf: ["agent.reach_changed"],
    noop: "every field named equals the reach in force",
  },
  "agentReach.requestAccess": {
    always: [],
    oneOf: ["agent.reach_requested"],
    noop: "the caller already has a pending ask of this agent",
  },
  "agentReachRequests.grant": {
    always: ["agent.reach_request_decided"],
    when: { "agent.reach_changed": "the person is not yet named in the agent's Hears from" },
  },
  "agentReachRequests.decline": { always: ["agent.reach_request_decided"] },
  // STRUCTURE-CHANGED: one `structure.rerouted` per item still open; a take-over of a task is a
  // handoff (assigned, unassigned, handed off). Items already decided are not repeated.
  "structureChanges.reroute": {
    always: [],
    oneOf: ["structure.rerouted"],
    when: {
      "task.assigned": "a task item is taken over",
      "task.unassigned": "a task item is taken over",
      "task.handed_off": "a task item is taken over",
    },
    noop: "every item named is already decided or no longer open",
  },
  // SHARED-DES: a dry run, or a link set equal to the stored one, writes nothing. A change writes
  // its own event and, in the same transaction, the STRUCTURE-CHANGED record (kind serves) with
  // the leaders' notices.
  "seats.servesUpdate": {
    always: [],
    oneOf: ["seat.serves_changed"],
    when: {
      "structure.changed": "a link changed",
      "inbox_item.created": "a leader or a served scope's head is told",
      // SHARED-DES-2: the team channel follows talk on a team link.
      "channel.member_changed": "a talk link to a team moves a DE in or out of its channel",
    },
    noop: "a dry run, or every link and grant equals the stored ones",
  },
  // MODEL-POLICY: a PUT equal to the stored policy changes nothing and writes nothing.
  // COS-SLICE1
  // `company.updated` when the settings differ; `agent_profile.updated` per listed DE whose
  // digest switch or channel changes (its lead profile).
  "chiefOfStaff.put": {
    always: [],
    oneOf: ["company.updated", "agent_profile.updated"],
    noop: "the body equals what is stored",
  },
  "suggestions.accept": {
    always: ["suggestion.accepted"],
    when: {
      "task.created": "a task card: the reviewed task is created on the chosen board",
      "reminder.created": "a reminder card",
      "meeting.scheduled": "a meeting card: a meeting of the team's series at the chosen time",
    },
  },
  "suggestions.dismiss": { always: ["suggestion.dismissed"] },
  "modelPolicy.put": {
    always: [],
    oneOf: ["company.model_policy_changed"],
    noop: "the body equals the company's stored model policy",
  },
  // RUN-EFFORT: a changed map is a company settings change.
  "runEffort.put": {
    always: [],
    oneOf: ["company.updated"],
    noop: "the body equals the company's stored effort settings",
  },
  "operator.effortDefaults.put": {
    always: [],
    oneOf: ["effort_defaults.changed"],
    noop: "the body equals the defaults in force",
  },
  "modelAccess.put": {
    always: [],
    oneOf: ["company.model_access_changed"],
    noop: "the body equals the company's stored model access",
  },
  // RECALL-RETENTION-HOLD-FORGET: every successful call writes the one governance event (a request
  // the memory service cannot take yet is still recorded, with `waiting`).
  "memoryGov.act": { always: ["memory.governance_changed"] },
  "memoryForget.step": { always: ["memory.governance_changed"] },
  // RECALL-PEOPLE-SURFACES
  "memoryMe.act": { always: ["memory.governance_changed"] },
  "memoryRemember.post": { always: ["memory.governance_changed"] },
  "memoryOffRecord.set": { always: ["memory.governance_changed"] },
  "operator.memoryGov.retentionPut": { always: ["memory.governance_changed"] },
  "operator.memoryGov.jobClose": { always: ["memory.governance_changed"] },
  "operator.memoryGov.holdPlace": { always: ["memory.governance_changed"] },
  "operator.memoryGov.dryRun": { always: ["memory.governance_changed"] },
  "operator.memoryGov.hardDelete": { always: ["memory.governance_changed"] },
  "operator.deploys.overrideVerification": { always: ["verification.overridden"] },
  "modelCredentials.create": {
    always: ["model_credential.added", "subscription_attestation.recorded"],
  },
  // BYO-01: an active credential always has its live attestation (create records one and a
  // revoked credential can be neither re-attested nor revoked again), so both end one, always.
  "modelCredentials.attest": {
    always: ["subscription_attestation.recorded", "subscription_attestation.revoked"],
  },
  "modelCredentials.revoke": {
    always: ["model_credential.revoked", "subscription_attestation.revoked"],
  },
  "modelCredentials.update": {
    always: [],
    oneOf: ["model_credential.updated"],
    noop: "nothing changed",
  },
  "connections.create": { always: ["tool_connection.added"] },
  // M4-04: an OAuth token inside its refresh lead is refreshed before the probe runs.
  "connections.check": {
    always: ["tool_connection.checked"],
    when: {
      "tool_connection.refreshed": "an OAuth connection whose access token is due for refresh",
    },
  },
  "connections.refresh": { always: ["tool_connection.refreshed"] },
  "connections.revoke": {
    always: ["tool_connection.revoked"],
    when: {
      "crew_connection_grant.changed":
        "the connection served its owner's crew (mail or calendar): those grants end with it",
      // STEP1-OUTBOX
      "connection.grant_changed":
        "the connection was granted to a person, DE, seat or team: those grants end with it",
    },
  },
  "connections.oauthComplete": {
    always: [],
    oneOf: ["tool_connection.added", "tool_connection.reconnected"],
  },
  "crews.putConnections": { always: ["crew_connection_grant.changed"] },
  // STEP1-OUTBOX
  "outbox.retry": { always: ["outbox.retried"] },
  "outbox.cancel": {
    always: ["outbox.cancelled"],
    when: {
      "approval.executed":
        "the item came from an approval: its outcome becomes not applied (cancelled)",
      "wakeup.requested": "the item came from an approval a digital employee asked for",
    },
  },
  // INC2-INTAKE: the notify doors (a repeat writes nothing; an app notice for an account no
  // company subscribed is acknowledged and writes nothing), the dead letters, the rules
  "integrations.notify": {
    always: [],
    oneOf: ["inbound_event.received"],
    noop: "the same notice again (its delivery id or signed content was seen), or a Google sync",
  },
  "integrations.notifyApp": {
    always: [],
    oneOf: ["inbound_event.received"],
    noop: "the same notice again, or an account no company subscribed",
  },
  // GMAIL-P1
  "integrations.gmailPush": {
    always: [],
    oneOf: ["inbound_event.received"],
    noop: "the same Pub/Sub message again, or a mailbox nobody syncs",
  },
  "connections.mailSync.put": { always: ["tool_connection.mail_sync_changed"] },
  "oauthClients.put": { always: ["oauth_client.changed"] },
  "oauthClients.delete": { always: ["oauth_client.changed"] },
  "intake.retryJob": { always: ["integration_job.resolved"] },
  "intake.discardJob": { always: ["integration_job.resolved"] },
  "intake.putRule": { always: ["inbound_rule.changed"] },
  "intake.deleteRule": { always: ["inbound_rule.changed"] },
  "connectionGrants.create": { always: ["connection.grant_changed"] },
  // INC2-CONTACT
  "contacts.upsert": {
    always: ["contact.upserted"],
    when: {
      "contact.identity_linked": "the input carried an outside system's id new to the contact",
      "contact.consent_recorded": "the input carried consent that changes a channel's latest",
    },
  },
  "contacts.linkExternal": {
    always: [],
    oneOf: ["contact.identity_linked"],
    noop: "the id was already linked to this contact",
  },
  "contacts.recordConsent": { always: ["contact.consent_recorded"] },
  // GHL-PROVIDER: the connector-mode CRM
  "ghlConnections.create": {
    always: [],
    oneOf: ["tool_connection.added"],
    noop: "an existing OAuth connection activated for its location (no new connection)",
  },
  "crm.act": {
    always: [],
    oneOf: ["outbox.queued"],
    noop: "the act needs a person's approval: nothing is written, the caller files the approval",
  },
  // INC2-ACCESS
  "access.apply": {
    always: [],
    oneOf: ["connection.grant_changed"],
    noop: "every cell was already at its level",
  },
  "connectionGrants.revoke": {
    always: [],
    oneOf: ["connection.grant_changed"],
    noop: "the grant was already revoked",
  },
  "runs.recordVerdict": { always: ["run.verdict_recorded"] },
  "runs.recordDecisions": { always: ["run.decision_recorded"] },
  "runs.escalatePremise": { always: ["run.premise_escalated", "run.waiting"] },
  "runs.resume": {
    always: ["run.resumed"],
    when: { "run.decision_recorded": "the run had a verdict (cleared as `verdict_reset`)" },
  },
  "digitalEmployees.requestDriftAudit": { always: ["drift_audit.scheduled"] },
  "agents.deploySelfServe": {
    always: ["deploy.staged"],
    when: {
      "de_template_override.changed":
        "the body names standing orders or a role-to-model map that differ from the DE's override",
    },
  },
  "ingest.post": {
    always: [],
    oneOf: [
      "activity.recorded",
      "outcome.recorded",
      "cost.posted",
      "time_entry.created",
      "time_entry.updated",
      "time_entry.approval_changed",
      "time_entry.deleted",
    ],
    noop: "a batch of telemetry items only (MONITORING-P1: stored in cell.telemetry_event, not the event log)",
  },
  // M7-03: one notice per Recall event id; its actions are the `recall.callbacks` consumer's.
  "recall.callbacks": {
    always: [],
    oneOf: ["recall.notice_received"],
    noop: "the Recall event id was recorded before, or its kind is not one CompanyOS acts on",
  },
  // M7-04: a promotion decided once (a decided one is 409); its approval follows it.
  "promotions.accept": {
    always: ["page.created", "promotion.accepted"],
    when: { "approval.decided": "the promotion has an approval still pending" },
  },
  "promotions.reject": {
    always: ["promotion.rejected"],
    when: { "approval.decided": "the promotion has an approval still pending" },
  },
  // RECALL-BRIEFS: closing the catch-up card is the person's own view state, not a company event.
  "memory.catchUpDismiss": { always: ["memory.catch_up_closed"] },
  // RECALL-ROLES-ONBOARDING: each act is recorded as the person's or admin's own decision.
  "memoryPeople.profileAccept": { always: ["memory.profile_decided"] },
  "memoryPeople.profileDiscard": { always: ["memory.profile_decided"] },
  "memoryPeople.successionAnswer": {
    always: ["memory.succession_answered"],
    when: { "memory.acting_ended": "the accepted seat had a previous holder" },
  },
  "memoryPeople.successionSettle": {
    always: ["memory.succession_settled"],
    when: { "memory.acting_ended": "the applied seat had a previous holder" },
  },
  "memoryPeople.actingEnd": { always: ["memory.acting_ended"] },
  "memoryPeople.onboardingStart": {
    always: [],
    // A Chief of Staff was there to ask the first question.
    oneOf: ["memory.onboarding_changed"],
    // The Chief of Staff's first question, posted in its chat with the person.
    when: { "message.created": "the onboarding began (the first question is asked in chat)" },
    noop: "the onboarding is already running or done, or there is no Chief of Staff yet",
  },
  "memoryPeople.onboardingDismiss": {
    always: [],
    // The person's quick card was open.
    oneOf: ["memory.onboarding_changed"],
    noop: "the flow cannot be put aside, or the card is already closed",
  },
  "memoryPeople.charterSet": { always: ["memory.charter_set"] },
  "memoryPeople.charterFromVision": {
    always: [],
    // The Vision has text.
    oneOf: ["memory.charter_set"],
    noop: "the Vision is empty",
  },
  // RECALL-KB: the switch and "Sync now" are recorded as the space's configuration change.
  "recall.kbUpdate": { always: ["recall.kb_configured"] },
  "recall.kbSyncNow": { always: ["recall.kb_configured"] },
  // MEMORY-HYGIENE: one audit event per bulk settle, whatever it settled (zero included).
  "memory.proposalsBulk": { always: ["memory_proposals.settled"] },
  // CAPTURE-LESS-NOISE: the company's capture override, like the loop breaker's.
  "memory.captureUpdate": {
    always: [],
    oneOf: ["company.updated"],
    noop: "the override is already as asked",
  },
  // RECALL-CONTRADICTIONS: a person's verdict on one contradiction.
  "memory.conflictSettle": { always: ["memory_contradiction.settled"] },
  // RECALL-INBOX-LOOP: every answer is audited; so is every switch change.
  "memory.laneDecide": {
    always: ["memory_lane.decided"],
    when: { "decision.outcome_answered": "the card is a decision's check" },
  },
  "memory.laneBulk": {
    always: ["memory_lane.bulk_approved"],
    when: { "memory_lane.decided": "at least one card is approved" },
  },
  "memory.laneSeeAll": { always: ["memory_lane.see_all_changed"] },
  // RECALL-ADMIN-MEMORY: a done or refused act is audited; one that waits on the memory service
  // changes nothing and says nothing.
  "memory.adminRecordAct": {
    always: [],
    oneOf: ["memory_admin.acted"],
    noop: "the memory service cannot take it yet: nothing changes (answered `waiting`)",
  },
  "memory.adminCharter": {
    always: [],
    oneOf: ["memory_admin.acted"],
    noop: "the memory service cannot take it yet: nothing changes (answered `waiting`)",
  },
  "memory.adminAgentAccess": {
    always: [],
    oneOf: ["memory_admin.acted"],
    noop: "the memory service cannot take it yet: nothing changes (answered `waiting`)",
  },
  "memory.adminRuleAct": {
    always: [],
    oneOf: ["memory_admin.acted"],
    noop: "the memory service cannot take it yet: nothing changes (answered `waiting`)",
  },
  "memory.adminJobFile": {
    always: [],
    oneOf: ["memory_admin.job_changed"],
    noop: "the same job is already open: it is the same job",
  },
  // PLAN-CHECK: every check is a step in the run's timeline.
  "memory.agentPlanCheck": { always: ["run.step_recorded"] },
  // TASK-FILES: every file write is in the task's activity; an agent's is in its run's timeline.
  "files.upload": {
    always: ["task.file_changed"],
    when: {
      "task.file_folder_changed": FOLDER_CHAIN_WHEN,
      "run.step_recorded": FILE_STEP_WHEN,
    },
  },
  "files.update": {
    always: [],
    oneOf: ["task.file_changed"],
    noop: "nothing changed (the file is returned as it is)",
    when: {
      "task.file_folder_changed": "a `/` in the new name names a folder the task does not have yet",
      "run.step_recorded": FILE_STEP_WHEN,
    },
  },
  "files.delete": {
    always: [],
    oneOf: ["task.file_changed"],
    noop: "the file is already in the trash",
    when: { "run.step_recorded": FILE_STEP_WHEN },
  },
  "files.restore": {
    always: [],
    oneOf: ["task.file_changed"],
    noop: "the file is not in the trash",
    when: { "run.step_recorded": FILE_STEP_WHEN },
  },
  "files.emptyTrash": {
    always: [],
    oneOf: ["task.trash_emptied"],
    noop: "the task's trash is empty",
  },
  "files.createFolder": { always: ["task.file_folder_changed"] },
  "files.updateFolder": {
    always: [],
    oneOf: ["task.file_folder_changed"],
    noop: "the name and parent are unchanged",
  },
  "files.deleteFolder": {
    always: ["task.file_folder_changed"],
    when: { "task.file_changed": "the subtree held live files (each is trashed)" },
  },
  "agentFiles.upload": {
    always: ["task.file_changed"],
    when: {
      "task.file_folder_changed": FOLDER_CHAIN_WHEN,
      "run.step_recorded": FILE_STEP_WHEN,
    },
  },
  "agentFiles.rename": {
    always: [],
    oneOf: ["task.file_changed"],
    noop: "the name is unchanged",
    when: {
      "task.file_folder_changed": "a `/` in the new name names a folder the task does not have yet",
      "run.step_recorded": FILE_STEP_WHEN,
    },
  },
  "agentFiles.delete": {
    always: [],
    oneOf: ["task.file_changed", "approval.requested"],
    noop: "the file is already in the trash",
    when: { "run.step_recorded": FILE_STEP_WHEN },
  },
  // FOUNDER-OS: the account with its portfolio company, the founder seat (role, seat, the
  // founder assigned) and the owner tuple, all in the new portfolio company's log.
  "accounts.create": {
    always: [
      "account.created",
      "company.created",
      "membership.created",
      "access.granted",
      "role.created",
      "seat.created",
      "seat.assigned",
    ],
  },
  "accounts.companies.add": {
    always: ["portfolio.company_added"],
    when: {
      "linked_seat.created": "the company has a head seat (a seat with no supervisor)",
      // The company is projected in its own log at once (the Focus row and the cascade read it).
      "portfolio_projection.published": "a projection of the company changed since its last one",
      // Wave 12: `create` makes a new company the caller owns first (its CEO seat, the caller
      // in it, the KPI catalog and the templates).
      "company.created": "the body is `create` (a new company)",
      "membership.created": "the body is `create` (the caller's owner membership)",
      "access.granted": "the body is `create` (the owner tuple, then the seat holder's)",
      "role.created": "the body is `create` (the CEO role)",
      "seat.created": "the body is `create` (the CEO seat)",
      "seat.assigned": "the body is `create` (the caller in the CEO seat)",
      "metric_def.created": "the body is `create` (the company's KPI catalog)",
      "template.created": "the body is `create` (the company's templates)",
    },
  },
  "portfolio.companies.remove": {
    always: ["portfolio.company_removed"],
    when: {
      "linked_seat.removed": "the company's head seat was linked",
      "goal.uncascaded": "a portfolio priority was cascaded into one of its priorities",
    },
  },
  // FOUNDER-OS phase 1: re-parenting a linked card (a company without a link gets one).
  // Every successful call records one: `linked_seat.moved` when the company already had a linked
  // head seat (also onto the seat it hangs under: the placement, confirmed), else
  // `linked_seat.created`.
  "portfolio.linkedSeats.update": {
    always: [],
    oneOf: ["linked_seat.moved", "linked_seat.created"],
  },
  "portfolio.goalLinks.create": { always: ["goal.cascaded"] },
  "portfolio.goalLinks.delete": { always: ["goal.uncascaded"] },
  "portfolio.briefs.generate": { always: ["founder_brief.generated"] },
  // CORE-FREEZE: a freeze opens its incident; lifting it resolves it (runs released, no event).
  "freeze.company": { always: ["incident.opened"] },
  "freeze.digitalEmployee": { always: ["incident.opened"] },
  "incidents.resolve": { always: ["incident.resolved"] },
  // CORE-REVERT: a revert always lists what it did; the rest happens per item it reaches.
  "incidents.revert": { always: ["incident.reverted"], when: REVERT_WHEN },
  "runs.revert": {
    always: ["incident.reverted"],
    when: {
      ...REVERT_WHEN,
      "incident.opened": "the run had no reversal incident yet (its first revert)",
    },
  },
  // Parity P1: the compensation runs through the normal writer, which writes its own event.
  "undo.apply": {
    always: [],
    oneOf: ["task.undone"],
    noop: "the caller had already undone it (the first outcome is answered again)",
    when: {
      "task.status_changed": "the undo reverses a status change",
      "task.updated": "the undo reverses a title, parent, start or estimate change",
      "task.due_changed": "the undo reverses a due date change",
      "task.priority_changed": "the undo reverses a priority change",
      "task.field_changed": "the undo reverses a field value",
      "task.moved": "the undo moves the task back to its board",
    },
  },
  // Parity P5 wave 1: a person's write applies (its own event); an agent's is filed as an
  // `automation` approval (`approval.requested`) and changes nothing yet.
  "automations.create": { always: [], oneOf: ["automation.created", "approval.requested"] },
  "automations.update": {
    always: [],
    oneOf: ["automation.updated", "approval.requested"],
    noop: "every field sent equals the rule's",
  },
  "automations.delete": { always: [], oneOf: ["automation.deleted", "approval.requested"] },
  "automations.enable": {
    always: [],
    oneOf: ["automation.updated", "approval.requested"],
    noop: "the rule is already on and not paused",
  },
  "automations.disable": {
    always: [],
    oneOf: ["automation.updated", "approval.requested"],
    noop: "the rule is already off",
  },
  "automations.duplicate": { always: [], oneOf: ["automation.created", "approval.requested"] },
  "seats.routinesCreate": {
    always: [],
    oneOf: ["recurring_work.created", "approval.requested"],
    when: { "responsibility.created": "a person names a new responsibility" },
  },
  "tasks.repeat": {
    always: ["recurring_work.created"],
    when: { "responsibility.created": "a new responsibility is named" },
  },
  "recurringWork.pause": { always: ["recurring.paused", "recurring_work.updated"] },
  "recurringWork.resume": { always: ["recurring.resumed", "recurring_work.updated"] },
  "recurringWork.skip": { always: ["recurring.skipped", "recurring_work.updated"] },
  "recurringWork.takeOver": {
    always: ["recurring.taken_over"],
    when: {
      "recurring_work.updated":
        "all future occurrences (the task template's assignees change, or are handed back)",
      "task.assigned": "this occurrence, and the seat taking over was not assigned to it",
      "task.unassigned": "this occurrence, and the routine's own seat was assigned to it",
    },
  },
  // Parity P1 wave 1: prefs, task layouts, field rules, task observations.
  "me.prefs.put": {
    always: [],
    oneOf: ["pref.changed"],
    noop: "the stored value is already this one",
  },
  "taskLayout.board.put": { always: ["board.task_layout_changed"] },
  "taskLayout.board.restore": { always: ["board.task_layout_changed"] },
  "taskLayout.space.put": { always: ["space.default_task_layout_changed"] },
  "taskLayout.space.restore": { always: ["space.default_task_layout_changed"] },
  "fields.rule": {
    always: [],
    oneOf: ["field.required_changed"],
    noop: "the field already has this rule",
  },
  // BILLING-PLANS: recurring plans, invoices, quotes, builds (the Stripe side lands through the
  // webhook; these are the operator's and the client's own writes).
  "billing.quotes.accept": {
    always: ["quote.changed"],
    when: {
      "invoice.created": "the quote is not a build project's (its invoice is mirrored)",
      "invoice.finalized": "Stripe returned the accepted quote's invoice finalised",
      "project.changed": "the quote belongs to a build project still quoted (it becomes accepted)",
      "invoice.paid": "Stripe charged the card on file when it finalised the quote's invoice",
    },
  },
  "billing.topupInvoices.create": {
    always: ["wallet.topup_invoiced", "invoice.created", "invoice.finalized"],
    when: {
      "invoice.paid": "a card company: the card on file was charged at once",
      "invoice.payment_failed": "a card company: the card on file was declined",
    },
  },
  "operator.billing.plans.create": { always: ["billing.price_changed"] },
  "operator.billing.plans.retire": { always: ["billing.price_changed"] },
  "operator.billing.planModelAccess.put": {
    always: [],
    oneOf: ["billing.price_changed"],
    noop: "the plan's setting already has that value",
  },
  "operator.billing.modelProviderMap.put": {
    always: [],
    oneOf: ["billing.price_changed"],
    noop: "the map already has exactly those entries",
  },
  "operator.billing.maintenancePlans.create": { always: ["billing.price_changed"] },
  "operator.billing.maintenancePlans.retire": { always: ["billing.price_changed"] },
  "operator.billing.catalog.create": { always: ["billing.price_changed"] },
  "operator.billing.catalog.update": { always: ["billing.price_changed"] },
  "operator.billing.catalog.delete": { always: ["billing.price_changed"] },
  "operator.billing.defaults.put": { always: ["billing.price_changed"] },
  "operator.billing.templates.put": { always: ["billing.price_changed"] },
  "operator.billing.subscription.put": {
    always: [],
    oneOf: ["subscription.created", "subscription.updated"],
    when: {
      "de_maintenance.changed":
        "the subscription is created and the company has DEs (each gets its maintenance line)",
      "subscription.quantity_changed":
        "the people count or a tier's DE count on the subscription changed",
      "invoice.created":
        "the subscription is created, or a mid-period change raises the bill (its prorated increase is invoiced at once)",
      "invoice.finalized": "the first invoice, or a mid-period increase's invoice, is finalized",
      "invoice.paid": "that invoice is charged to a card that pays",
      "invoice.payment_failed": "that invoice is charged to a card that declines",
      "subscription.past_due": "that invoice's charge declined",
      "cost.posted": "that paid invoice carries DE maintenance (posted per DE)",
    },
    noop: "the plan, prices, trial and grace days are already these",
  },
  "operator.billing.subscription.retry": {
    always: [],
    oneOf: ["invoice.paid", "invoice.payment_failed"],
    when: {
      "subscription.restored": "the retried invoice was paid and nothing else is past due",
      "digital_employee.resumed": "the company is restored and had DEs paused for `subscription`",
      "cost.posted": "the paid invoice carried DE maintenance (posted per DE)",
    },
  },
  "operator.billing.subscription.cancel": {
    always: ["subscription.cancelled"],
    when: {
      "subscription.restored":
        "a past-due or suspended subscription is cancelled now (its pauses are lifted)",
      "digital_employee.resumed": "cancelled now with DEs paused for `subscription`",
      "de_maintenance.changed": "cancelled now: each live maintenance line ends",
    },
  },
  "operator.billing.collection.put": {
    always: [],
    oneOf: ["billing.account_changed"],
    noop: "the collection method, terms, methods, email and setup fee are already these",
  },
  "operator.billing.discounts.create": {
    always: ["discount.granted"],
    when: {
      "subscription.updated":
        "it starts today and the company has a live subscription (applied now)",
    },
  },
  "operator.billing.discounts.end": { always: ["discount.ended"] },
  // (an operator's open alert about it is closed too: `inbox_item.done` in HTVA's own company)
  "operator.billing.operations.retry": { always: ["billing_operation.retried"] },
  "operator.billing.operations.dismiss": { always: ["billing_operation.dismissed"] },
  "operator.billing.maintenance.put": {
    always: [],
    oneOf: ["de_maintenance.changed"],
    when: {
      "subscription.quantity_changed":
        "the company has a live subscription (both tiers' counts move)",
      "invoice.created":
        "an upgrade on a live subscription: its prorated increase is invoiced at once (a downgrade waits for the renewal)",
      "invoice.finalized": "that upgrade's invoice is finalized",
      "invoice.paid": "that invoice is charged to a card that pays",
      "invoice.payment_failed": "that invoice is charged to a card that declines",
      "subscription.past_due": "that invoice's charge declined",
      "cost.posted": "that paid invoice carries the DE's maintenance (posted per DE)",
    },
    noop: "the DE is already on this tier",
  },
  "operator.billing.invoices.create": {
    always: ["invoice.created"],
    when: {
      "invoice.finalized": "`send` is true",
      "invoice.paid": "`send` is true for a card company and the card was charged",
      "invoice.payment_failed": "`send` is true for a card company and the card was declined",
    },
  },
  // at least one: emailed (invoice collection), finalised (it was still a draft), or a card
  // company's charge, paid or declined
  "operator.billing.invoices.send": {
    always: [],
    oneOf: ["invoice.sent", "invoice.finalized", "invoice.paid", "invoice.payment_failed"],
  },
  "operator.billing.invoices.remind": { always: ["invoice.reminded"] },
  // BILLING-LEDGER
  "operator.billing.provider.put": {
    always: [],
    oneOf: ["billing.account_changed"],
    noop: "the company's choice is that provider already",
  },
  // lane D: the month-end close is recorded in the ledger state, and says so in the log
  "operator.billing.close.record": { always: ["billing.period_closed"] },
  "operator.billing.ledger.failPayment": {
    always: ["invoice.payment_failed"],
    when: {
      "subscription.past_due":
        "the subscription's invoice, and the subscription was not past due yet",
    },
  },
  "operator.billing.ledger.renewNow": {
    always: ["invoice.created", "invoice.finalized", "subscription.updated"],
  },
  "operator.billing.invoices.markPaid": {
    always: ["invoice.marked_paid", "invoice.paid"],
    when: {
      "subscription.restored":
        "it was the subscription invoice holding the company past due or suspended",
      "digital_employee.resumed": "the company is restored and had DEs paused for `subscription`",
      "cost.posted": "a subscription invoice carrying DE maintenance (posted per DE)",
    },
  },
  "operator.billing.invoices.void": {
    always: ["invoice.voided"],
    when: {
      "subscription.restored":
        "it was the subscription invoice holding the company past due or suspended",
      "digital_employee.resumed": "the company is restored and had DEs paused for `subscription`",
    },
  },
  "operator.billing.quotes.create": { always: ["quote.changed"] },
  "operator.billing.quotes.send": { always: ["quote.changed"] },
  "operator.billing.quotes.cancel": { always: ["quote.changed"] },
  "operator.billing.projects.create": { always: ["project.changed"] },
  "operator.billing.projects.update": {
    always: [],
    oneOf: ["project.changed"],
    noop: "the status, delivery date and maintenance shape are already these",
  },
  "operator.billing.milestones.invoice": {
    always: ["project.milestone_invoiced", "invoice.created"],
    when: {
      "invoice.finalized": "`send` is true",
      "invoice.paid": "`send` is true for a card company and the card was charged",
      "invoice.payment_failed": "`send` is true for a card company and the card was declined",
      "project.changed": "the project was accepted (it moves to in progress)",
    },
  },
  "tasks.observations.create": {
    always: ["task.observation_recorded"],
    when: {
      "task.field_changed":
        "the board has a number field named after the metric (any case) and the metric's newest live value changed it",
    },
  },
  // BOARD-RESULTS: a published card's results; a board's research signal setup.
  "tasks.results.record": {
    always: [],
    oneOf: ["task.observation_recorded"],
    noop: "every reading equals the window's live one (the publication row is still upserted)",
    when: {
      "task.field_changed":
        "the board has a number field named after a reading's metric and its newest live value changed it",
    },
  },
  "boards.researchSignals.setup": {
    always: [],
    oneOf: ["field_def.created", "automation.created"],
    noop: "the board already has every signal field and the Expires rule (a repeated setup)",
  },
  // TEMPLATES-T3/T4: overlays, vertical packs, deploy from a role, access, probation.
  "templateOverlays.create": { always: ["template_overlay.changed"] },
  "templateOverlays.update": { always: ["template_overlay.changed"] },
  "templateOverlays.archive": { always: ["template_overlay.changed"] },
  "verticalPacks.apply": { always: ["template_overlay.changed"] },
  "digitalEmployees.applyRole": {
    always: ["digital_employee.role_applied"],
    when: {
      "de_access_default.changed": "the DE holds or closes access on at least one family",
      "metric_def.created": "the company did not define one of the role's KPIs yet",
      "agent_policy.changed":
        "the role, the pack or an overlay sets an enforced task class tighter than the seat holds",
      "responsibility.created": "a routine board is named and the role has a cadence",
      "recurring_work.created": "a routine board is named and the role has a cadence",
    },
  },
  "digitalEmployees.putAccess": {
    always: [],
    oneOf: ["de_access_default.changed"],
    noop: "every family is already at the level sent",
  },
  "digitalEmployees.endProbation": { always: ["digital_employee.probation_ended"] },
  // GUIDED-FLOW: a step save writes each value through its real field's own event.
  "flows.send": {
    always: ["flow.sent"],
    when: { "invite.created": "the flow is sent with an invitation to the seat (`invite`)" },
  },
  "flows.agentSend": { always: ["flow.sent"] },
  "flows.saveStep": {
    always: ["flow.step_saved"],
    when: {
      "seat.updated":
        "a value bound to the seat (title, role, reports to, staff, a setting) changed",
      "digital_employee.updated":
        "a value bound to the DE's profile (role, goal, backstory) changed",
      "sensitive_value.sealed": "the secure step was saved with at least one value",
      "invite.updated": "the access level of a pending invitation changed",
    },
  },
  "flows.submit": {
    always: ["flow.completed"],
    when: {
      "seat.assigned":
        "seat setup completed by a person who does not hold the seat, and no person does",
      "access.granted": "seat setup assigned its person to the seat (the holder relation)",
      "team.updated": "that assignment changes the composition of a team the seat is in",
      "digital_employee.job_changed": "DE onboarding completed while the DE's job was unsubmitted",
    },
  },
  "flows.cancel": { always: ["flow.cancelled"] },
  // CANVAS-BLOCKS: a click is a canvas op row (the agent reads it back with `show_user
  // {op: context}`) and its record.
  "liveCanvas.interact": { always: ["live.interacted"] },
} as const satisfies Record<WriteRouteId, Obligation>;

/** Every event an obligation names, in any role. */
export function obligationEvents(o: Obligation): EventType[] {
  return [...o.always, ...(o.oneOf ?? []), ...(Object.keys(o.when ?? {}) as EventType[])];
}

/**
 * Disagreements between route `emits` and obligations: a write route without an obligation,
 * an obligation without a route or for a read, a success that writes nothing, an obligation
 * event the route does not declare, or a declared event no obligation accounts for.
 */
export function obligationDrift(
  routeDefs: Readonly<Record<string, RouteDef>>,
  obligations: Readonly<Record<string, Obligation>>,
): string[] {
  const problems: string[] = [];
  for (const [id, route] of Object.entries(routeDefs)) {
    const o = obligations[id];
    if (isReadRoute(route)) {
      if (o) problems.push(`${id}: read with an obligation`);
      continue;
    }
    if (!o) {
      problems.push(`${id}: write route without an obligation`);
      continue;
    }
    if (o.always.length === 0 && (o.oneOf ?? []).length === 0)
      problems.push(`${id}: a successful call may write no event`);
    if (o.noop !== undefined && (o.always.length > 0 || !o.noop.trim()))
      problems.push(`${id}: a noop needs a reason and no \`always\` events`);
    const named = obligationEvents(o);
    const emits = new Set<string>(route.emits);
    for (const t of named)
      if (!emits.has(t)) problems.push(`${id}: ${t} required but not in emits`);
    for (const t of route.emits)
      if (!named.includes(t)) problems.push(`${id}: emits ${t} without an obligation`);
    if (new Set(named).size !== named.length) problems.push(`${id}: event listed twice`);
  }
  for (const id of Object.keys(obligations))
    if (!routeDefs[id]) problems.push(`${id}: obligation for an unknown route`);
  return problems;
}

/**
 * What one exercised call emitted, against its obligation (review #3). `expectedWhen` names the
 * conditional events whose condition held in that scenario; each must then be emitted, and
 * every other conditional event must not be. Returns the problems (empty = conforming).
 */
export function checkEmitted(
  route: Pick<RouteDef, "emits">,
  o: Obligation,
  emitted: readonly string[],
  expectedWhen: readonly string[],
): string[] {
  const have = new Set(emitted);
  const problems: string[] = [];
  for (const t of o.always) if (!have.has(t)) problems.push(`missing ${t}`);
  const noop = o.noop !== undefined && have.size === 0;
  if (o.oneOf?.length && !noop && !o.oneOf.some((t) => have.has(t)))
    problems.push(`none of ${o.oneOf.join("|")}`);
  const conditional = Object.keys(o.when ?? {});
  for (const t of expectedWhen)
    if (!conditional.includes(t)) problems.push(`${t} is not a conditional event`);
  for (const t of conditional) {
    if (expectedWhen.includes(t) && !have.has(t)) problems.push(`missing ${t} (condition held)`);
    if (!expectedWhen.includes(t) && have.has(t))
      problems.push(`unexpected ${t} (condition did not hold)`);
  }
  const declared = new Set<string>(route.emits);
  for (const t of have) if (!declared.has(t)) problems.push(`undeclared ${t}`);
  return problems;
}

/**
 * Conditional events not exercised both ways by a route's scenarios: each `when` event needs a
 * scenario where its condition holds and one where it does not.
 */
export function uncoveredConditions(
  o: Obligation,
  scenarios: readonly { when: readonly string[] }[],
): string[] {
  const missing: string[] = [];
  for (const t of Object.keys(o.when ?? {})) {
    if (!scenarios.some((s) => s.when.includes(t))) missing.push(`${t}: never held`);
    if (!scenarios.some((s) => !s.when.includes(t))) missing.push(`${t}: always held`);
  }
  return missing;
}

/** Catalog types with no producer: no route emits them and no background job does. */
export function orphanEventTypes(
  types: readonly string[],
  routeDefs: Readonly<Record<string, RouteDef>>,
  background: Readonly<Record<string, readonly string[]>>,
): string[] {
  const produced = new Set<string>([
    ...Object.values(routeDefs).flatMap((r) => [...r.emits]),
    ...Object.values(background).flat(),
  ]);
  return types.filter((t) => !produced.has(t));
}
